import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  TextField,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  Divider,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  AccessTime,
  Flag,
  FlagOutlined,
  ArrowBack,
  ArrowForward,
  Schedule,
  Bookmark,
  Edit as EditIcon,
  Straighten as StraightenIcon,
  Adjust as AdjustIcon,
  ExpandMore,
  ImageNotSupported,
  PauseCircle,
  PlayCircle,
} from '@mui/icons-material';
import { ProtractorIcon, SetSquareIcon } from '../../../components/measurement/MeasurementToolIcons';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api, { resolveQuestionHtmlAssetUrl } from '../../../api';
import useExamMode from '../../../hooks/useExamMode';
import HtmlWithLatex from '../../QuestionBank/components/HtmlWithLatex';
import FormatInstructions from '../../../components/FormatInstructions';
import QuestionPaperLayout from '../../QuestionBank/components/QuestionPaperLayout';
import EnglishErrorFindingLayout from '../../QuestionBank/components/EnglishErrorFindingLayout';
import EnglishClozePassageLayout from '../../QuestionBank/components/EnglishClozePassageLayout';
import QuestionAnnotationCanvas from './components/QuestionAnnotationCanvas';
import PassageWithLineNumbers from '../../QuestionBank/components/PassageWithLineNumbers';
import { computePassageWrapWidth } from '../../QuestionBank/utils/passageLineExtract';
import {
  isErrorFindingStandalone,
  isErrorFindingContext,
  isClozeWordChoice,
} from '../../QuestionBank/utils/englishPaperDetection';
import {
  cleanStemText,
  extractSubCore,
  isComprehensionPassage,
  toSubLabel,
  countBlanksInText,
  groupedAnswerHasContent,
} from '../utils/groupedQuestionUtils';
import { interactiveWrittenOf, interactiveArrayOf, interactiveValueOf, interactiveWrittenText } from '../utils/interactiveAnswerValue';
import { isFillQuestionType, getDisplayFillMarksTotal } from '../../../utils/fillQuestionUtils';
import { TypeDrawInput, ScratchPad, MathInputTools } from '../components/TypeDrawQuestionInput';
import ImageOverlayFillInput from '../components/ImageOverlayFillInput';
import InteractiveDiagramCanvas from '../components/InteractiveDiagramCanvas';
import MeasurementWorkspace from '../../../components/measurement/MeasurementWorkspace';
import { MeasurementToolsProvider, AnnotateProvider } from '../../../components/measurement/MeasurementToolsContext';
import VirtualProtractorOverlay from '../../../components/measurement/VirtualProtractorOverlay';
import VirtualRulerOverlay from '../../../components/measurement/VirtualRulerOverlay';
import VirtualSetSquareOverlay from '../../../components/measurement/VirtualSetSquareOverlay';
import VirtualCompassOverlay from '../../../components/measurement/VirtualCompassOverlay';
import GeometryMarkSegment from '../../../components/measurement/GeometryMarkSegment';
import { getMeasurementMode, measurementModeForSub, getPxPerCm, MEASUREMENT_MODES } from '../../../utils/measurementQuestionDetect';
import { isMeasurementVerifyCorrect } from '../../../utils/measurementVerify';
import { isAnswerCorrect, requiresOrderedAnswer, decodeHtmlEntities, correctIsLabelSequence, correctIsEdgeRoute, submissionIsDeterministicallyWrong, isNumericPowerExpression, matchesNumericPower, bracketedCalculationMismatch, isNumericOnlyAnswerMode, setCustomAnswerUnits } from '../../../utils/answerMatchUtils';
import { compressDrawingForVerify, compositeWithContext, skipContextForDrawing, compressAnswerForSubmit } from '../../../utils/answerDrawingUtils';
import useContentVerification, { formatVerifierButtonLabel, getVerifierButtonColors } from '../../../hooks/useContentVerification';
import QuestionVerifyDialog from '../../../components/QuestionVerifyDialog';
import QuestionBankQuestionEditDialog from '../../QuestionBank/components/QuestionBankQuestionEditDialog';
import { testerHasPermission, isTesterUser } from '../../../utils/testerPermissions';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FillBlankUnitTextField, { blankMathToolsAdapters } from '../../QuestionBank/components/FillBlankUnitTextField';

import {
  resolveAnswerUnitProps,
  resolveBlankOrAnswerUnitProps,
  resolveCompoundUnits,
} from '../../../utils/answerUnitUtils';
import CompoundUnitInput from '../components/CompoundUnitInput';

// The mark scheme for this question is pure maths (answer_input_mode from
// QuestionBankAnswer::markSchemeIsNumericOnly), so the typed box refuses letters.
const numericOnlyInput = (question) => isNumericOnlyAnswerMode(question?.answer_data);

function flagQuillImageHandler() {
  const quill = this.quill;
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/png,image/jpeg,image/webp,image/gif');
  input.click();
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.post('/student/upload-flag-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.image_url;
      if (!url) throw new Error('No image URL');
      const range = quill.getSelection(true) || { index: quill.getLength() };
      quill.insertEmbed(range.index, 'image', url, 'user');
      quill.setSelection(range.index + 1);
    } catch {
      toast.error('Image upload failed');
    }
  };
}
let _flagReasonUploading = false;
async function processFlagReasonHtml(html, setter) {
  if (!html.includes('data:image/') || _flagReasonUploading) { setter(html); return; }
  _flagReasonUploading = true;
  let processed = html;
  for (const match of [...html.matchAll(/src="(data:image\/[^"]+)"/g)]) {
    try {
      const blob = await (await fetch(match[1])).blob();
      const file = new File([blob], `flag.${blob.type.split('/')[1] || 'png'}`, { type: blob.type });
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/student/upload-flag-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data?.data?.image_url;
      if (url) processed = processed.replace(match[0], `src="${url}"`);
    } catch { toast.error('Image upload failed'); }
  }
  _flagReasonUploading = false;
  setter(processed);
}
const FLAG_QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['link', 'image'],
      ['clean'],
    ],
    handlers: { image: flagQuillImageHandler },
  },
};
const FLAG_QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'script', 'list', 'bullet', 'indent', 'link', 'image'];

// "Example Questions" is an internal grouping label for practice questions appended to an exam — it is
// not a real exam section and must not show as a heading in the student take view. Genuine sections
// (comprehension "Section A", etc.) are kept. Mirrors StudentPaperTake.jsx.
const displaySectionTitle = (title) => {
  const t = String(title ?? '').trim();
  if (!t || /^example\s+questions?$/i.test(t)) return null;
  return t;
};

const formatCorrectAnswerForDisplay = (answer) => {
  if (answer == null) return "";
  let parsed = answer;
  if (typeof answer === "string" && answer.trim().startsWith("[")) {
    try { parsed = JSON.parse(answer); } catch {}
  }
  if (Array.isArray(parsed)) {
    const flat = parsed.flat(Infinity).map((v) => String(v ?? "").trim()).filter(Boolean);
    return flat.join(" / ");
  }
  return String(answer);
};

// Renders correct_answer with proper separation for essay questions (multiple topics).
// Only expands array items into numbered sections when isEssay=true; otherwise falls back to inline.
const renderCorrectAnswer = (answer, label, isEssay = false) => {
  let parsed = answer;
  if (typeof parsed === 'string' && parsed.trim().startsWith('[')) {
    try { parsed = JSON.parse(parsed); } catch {}
  }
  if (isEssay && Array.isArray(parsed) && parsed.length > 1) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.25 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        {parsed.map((topic, i) => (
          <Box key={i} sx={{ borderLeft: '3px solid', borderColor: 'inherit', pl: 1.5, py: 0.5, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: '0 4px 4px 0' }}>
            <Typography component="div" variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Option {i + 1}
            </Typography>
            <HtmlWithLatex html={String(topic ?? '')} />
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
      <span>{label}</span>
      <HtmlWithLatex html={formatCorrectAnswerForDisplay(answer)} />
    </Box>
  );
};

const isBoxModeImageFillQuestion = (question) => {
  const t = String(question?.type || question?.question_type || '').toLowerCase().replace(/_/g, '-');
  const overlay = question?.answer_data?.overlay_layout;
  return t === 'image-fill' && overlay?.mode !== 'inline'
    && Array.isArray(overlay?.blanks) && overlay.blanks.length > 0;
};

// Renders a box-mode image-fill correct answer as the overlay image with each box filled by its
// correct value (empty boxes still drawn) instead of a flat comma list. Falls back to the verify
// response's display string when the take payload withheld per-blank answers (single box → whole
// string; multi-box → split on | or ,).
// Image-only options (NVR nets, shape choices) carry the answer in the picture — the option label
// alone ("2") is meaningless without it. Multi-select answers have one image per correct option.
const renderCorrectOptionImages = (verifyResult, label = 'Correct answer:') => {
  const images = [
    ...(Array.isArray(verifyResult?.correct_option_images) ? verifyResult.correct_option_images : []),
    ...(verifyResult?.correct_option_image ? [verifyResult.correct_option_image] : []),
  ].filter(Boolean);
  const unique = Array.from(new Set(images));
  if (unique.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
      {!verifyResult.correct_answer && (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {unique.map((src, i) => (
          <Box
            key={`${src}_${i}`}
            component="img"
            src={src}
            alt={`Correct option ${i + 1}`}
            onError={(e) => { e.target.style.display = 'none'; }}
            sx={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'white', alignSelf: 'flex-start' }}
          />
        ))}
      </Box>
    </Box>
  );
};

const renderImageFillCorrectAnswer = (question, label = 'Correct answer:', fallbackRaw = '') => {
  const overlay = question?.answer_data?.overlay_layout;
  if (!overlay || !Array.isArray(overlay.blanks) || overlay.blanks.length === 0) return null;
  const blanks = overlay.blanks;
  const cleanVal = (v) => {
    const raw = Array.isArray(v) ? (v[0] ?? '') : (v ?? '');
    return decodeHtmlEntities(String(raw).replace(/&nbsp;/gi, ' ').replace(/<[^>]*>/g, '').trim());
  };
  const correctBlanks = question?.answer_data?.correct_blanks;
  const hasBlanks = Array.isArray(correctBlanks)
    && correctBlanks.some((b) => (Array.isArray(b) ? b.length > 0 : String(b ?? '').trim() !== ''));
  let revealValues;
  if (hasBlanks) {
    revealValues = blanks.map((_, i) => cleanVal(correctBlanks[i]));
  } else if (Array.isArray(fallbackRaw)) {
    // Mock-exam verify returns correct_answer as a per-blank array (empties preserved positionally).
    revealValues = blanks.map((_, i) => cleanVal(fallbackRaw[i]));
  } else {
    const rawStr = decodeHtmlEntities(String(fallbackRaw ?? '').replace(/&nbsp;/gi, ' ').replace(/<[^>]*>/g, '').trim());
    revealValues = blanks.length === 1
      ? [rawStr]
      : blanks.map((_, i) => (rawStr.split(/\s*[|,]\s*/)[i] ?? ''));
  }
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography>
      <ImageOverlayFillInput
        overlayLayout={overlay}
        values={revealValues}
        revealValues={revealValues}
        prefilled={question?.answer_data?.blank_labels}
        readOnly
      />
    </Box>
  );
};

// ---- Status constants ----
const STATUS = {
  NOT_VISITED: 'not-visited',
  UNANSWERED: 'unanswered',
  ANSWERED: 'answered',
  PARTIAL: 'partial',
  FLAGGED: 'flagged',
  ANSWER_LATER: 'answer-later',
};

const statusColors = {
  [STATUS.NOT_VISITED]: '#e0e0e0',
  [STATUS.UNANSWERED]: '#ef5350',
  [STATUS.ANSWERED]: '#4caf50',
  [STATUS.PARTIAL]: '#9ccc65',
  [STATUS.FLAGGED]: '#ff9800',
  [STATUS.ANSWER_LATER]: '#1565c0',
};

// ---- Format seconds to mm:ss ----
const formatTime = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

// ---- Type/Draw tabbed input imported from TypeDrawQuestionInput.jsx ----
// contextImageUrl: html2canvas snapshot of the question display — passed to TypeDrawInput
// so the drawing pad can composite the question chart/table behind the student's strokes
// for AI verification, without rendering it visually inside the pad.

const normalizeMathSymbols = (v) => String(v ?? '').replace(/÷/g, '/').replace(/×/g, '*');

const ARITHMETIC_VULGAR_FRACTIONS = {
  '½': ' 1/2 ', '⅓': ' 1/3 ', '⅔': ' 2/3 ', '¼': ' 1/4 ', '¾': ' 3/4 ',
  '⅕': ' 1/5 ', '⅖': ' 2/5 ', '⅗': ' 3/5 ', '⅘': ' 4/5 ', '⅙': ' 1/6 ',
  '⅚': ' 5/6 ', '⅐': ' 1/7 ', '⅛': ' 1/8 ', '⅜': ' 3/8 ', '⅝': ' 5/8 ',
  '⅞': ' 7/8 ', '⅑': ' 1/9 ', '⅒': ' 1/10 ',
};
const ARITHMETIC_SUPERSCRIPTS = { '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4', '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9' };

// Put a written calculation into one comparable form: ÷ × → / *, ⅙ → 1/6, 4² → 4^2, \times → *,
// \frac{1}{6} → 1/6, an ellipsis gap ("+ ... +") dropped. Mirrors StudentPaperTake.jsx.
const normalizeArithmeticText = (raw) => normalizeMathSymbols(String(raw ?? ''))
  .replace(/\\times/g, '*')
  .replace(/\\div/g, '/')
  .replace(/\\circ\b|°/g, '')
  .replace(/\\dots|\\ldots|…|\.\.\./g, ' ')
  .replace(/\\frac\s*\{\s*(-?\d+)\s*\}\s*\{\s*(\d+)\s*\}/g, ' $1/$2 ')
  .replace(/[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅐⅛⅜⅝⅞⅑⅒]/g, (ch) => ARITHMETIC_VULGAR_FRACTIONS[ch] ?? ch)
  .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, (ch) => `^${ARITHMETIC_SUPERSCRIPTS[ch]}`)
  .replace(/[£$%]/g, '')
  .replace(/[−–—]/g, '-')
  .replace(/(?<=\d)\s*x\s*(?=\d)/gi, '*');

// A "what calculation did you do?" mark scheme is a pure arithmetic expression — only numbers,
// operators (× ÷ + − * / ^), parentheses, "=" and spaces. Graded by structure, not AI Vision, which has
// accepted answers with an operator dropped ("(30 10) ÷ 50" for "(30 × 10) ÷ 50").
// Mirrors StudentPaperTake.jsx.
const arithmeticExpressionTokens = (raw, { requireCompound = true } = {}) => {
  const s = normalizeArithmeticText(raw).trim();
  if (s === '') return null;
  if (!/^[\d\s()+\-*/.^=]+$/.test(s)) return null;
  // Scope strictly to COMPOUND calculations — a multiplication (× / *) or parentheses ("(30 × 10) ÷ 50").
  // Equations ("180+180=360") also count even without ×/(, so prose that quotes the sum can match.
  if (requireCompound && !/[*(]/.test(s) && !s.includes('=')) return null;
  const operators = (s.match(/[+\-*/^=]/g) || []).slice().sort();
  const operands = (s.match(/\d+(?:\.\d+)?/g) || []).map((n) => String(parseFloat(n))).sort();
  return { operators, operands, hasOperator: /[+\-*/=]/.test(s) };
};

const sameArithmeticTokens = (a, b) => a.operators.join(' ') === b.operators.join(' ')
  && a.operands.join(' ') === b.operands.join(' ');

// Most mark schemes state the calculation inside a sentence ("The next line of the pattern is:
// 1² + 2² + 3² + 4² = 30 = ⅙ × 4 × 5 × 9"). Pull out the arithmetic runs so a student who writes the
// line out can still be checked operator-for-operator.
const arithmeticRunsInProse = (raw) => (normalizeArithmeticText(raw).match(/[\d\s()+\-*/.^=]{6,}/g) || [])
  .map((run) => arithmeticExpressionTokens(run.replace(/[\s.]+$/, '')))
  .filter(Boolean);

const arithmeticCalculationMismatch = (submitted, correct) => {
  // Brackets moved or dropped leave the operand and operator multisets below untouched, so the token
  // comparison cannot see it — the evaluated value can.
  if (bracketedCalculationMismatch(submitted, correct)) return true;
  const s = arithmeticExpressionTokens(submitted, { requireCompound: false });
  const c = arithmeticExpressionTokens(correct);
  if (c) {
    if (!s) return true;
    return !sameArithmeticTokens(s, c);
  }
  if (!s || !s.hasOperator) return null;
  const runs = arithmeticRunsInProse(correct);
  if (!runs.length) return null;
  if (runs.some((run) => sameArithmeticTokens(s, run))) return false;
  // Prose states the calculation but rarely IS the expected answer, so only the unambiguous signature
  // counts: every operand of a run reproduced, operators not — a dropped or swapped symbol.
  const droppedOperator = runs.some((run) => run.operands.join(' ') === s.operands.join(' ')
    && run.operators.join(' ') !== s.operators.join(' '));
  return droppedOperator ? true : null;
};

const arithmeticEquationMatchesProse = (submitted, correct) => {
  const s = arithmeticExpressionTokens(submitted, { requireCompound: false });
  if (!s || !s.hasOperator) return false;
  const runs = arithmeticRunsInProse(correct);
  if (runs.some((run) => sameArithmeticTokens(s, run))) return true;
  const compactS = normalizeArithmeticText(submitted).replace(/\s+/g, '');
  const compactC = normalizeArithmeticText(correct).replace(/\s+/g, '');
  return compactS.length >= 5 && compactC.includes(compactS);
};

// A mark scheme that is purely one or more colon-format clock times (e.g. "3:00", "3:00 or 9:00").
// Used to force strict, rule-based grading for time-of-day answers so dot/bare/o'clock forms
// (3.00, 3.0, 3, "3 o'clock") are not accepted.
const isPureClockTimeAnswer = (text) => {
  const t = String(text ?? '').trim();
  if (!t) return false;
  const parts = t.split(/\s+or\s+|\s*\/\s*|\s*,\s*/i).map((p) => p.trim()).filter(Boolean);
  return parts.length > 0 && parts.every((p) => /^\d{1,2}:\d{2}(\s*(am|pm))?$/i.test(p));
};

const isShortValueInProseAnswer = (submitted, prose) => {
  if (!submitted || !prose) return false;
  // A route / order of labelled points ("A E D B C G") is the answer itself, not prose explaining it:
  // every letter is required, so finding the first few of them ("A E") must not pass the whole route.
  if (correctIsLabelSequence(prose) || correctIsEdgeRoute(prose)) return false;
  if (prose.length <= submitted.length * 2) return false;
  // A comma-separated list of discrete values (e.g. "0.005, 0.024, 1.006, 1.01")
  // is a structured mark-scheme answer, not prose — matching a single one of its
  // tokens must not pass the whole answer. Only genuine sentences (a part with 2+
  // words) are treated as prose.
  const proseParts = prose.split(/,(?!\d{3}(?!\d))/);
  if (proseParts.length > 1 &&
      !proseParts.some((p) => p.trim().split(/\s+/).filter(Boolean).length >= 2)) {
    return false;
  }
  const normalized = normalizeMathSymbols(submitted.trim());
  const prosNorm = normalizeMathSymbols(prose);
  const checkPart = (val) => {
    if (!val) return false;
    const escaped = val.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:^|[\\s=:£(,])${escaped}(?=$|[\\s.,)£,:])`, 'i').test(prosNorm);
  };
  if (checkPart(normalized)) return true;
  const parts = normalized.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length > 1 && parts.every((p) => checkPart(p))) return true;
  return false;
};

const isTypedVerifyCorrect = (submitted, correct, question = null) => {
  const mode = question ? getMeasurementMode(question) : null;
  if (mode) {
    const measured = isMeasurementVerifyCorrect(submitted, correct, question, mode);
    if (measured !== null) return measured;
  }
  if (arithmeticEquationMatchesProse(submitted, correct)) return true;
  return isAnswerCorrect(submitted, correct, {
    ordered: requiresOrderedAnswer(question),
    questionText: question?.question_text ?? question?.text ?? question?.question ?? '',
  });
};

/**
 * Returns display properties for a verify-answer Alert.
 * - Correct (text rule)        → green success
 * - Correct (AI drawing)       → green success + ai_evaluated badge
 * - Incorrect (drawing, AI)    → orange warning (AI evaluated, may want to try typed answer)
 * - Needs review (AI conflict) → yellow warning + needs_review badge
 * - Incorrect (answered, text) → red error
 * - No answer entered          → blue info ("Correct answer:" without "Incorrect" label)
 */
const getVerifyDisplayProps = (result) => {
  if (!result) return null;
  if (result.is_correct) {
    return {
      severity: 'success',
      prefix: result.ai_evaluated ? 'Correct! ✓ (AI Vision)' : 'Correct! ✓',
      answerLabel: 'Correct answer:',
      aiEvaluated: !!result.ai_evaluated,
      needsReview: false,
    };
  }
  // Only a genuine 2-model disagreement (both vision models ran) is "models disagreed". The
  // no-reference fallback also sets needs_review but never ran the models — fall through so it
  // renders as a plain "compare with the correct answer" drawing result.
  if (result.needs_review && result.ai_evaluated) {
    return {
      severity: 'warning',
      prefix: 'Needs review — AI models disagreed',
      answerLabel: 'Correct answer:',
      aiEvaluated: true,
      needsReview: true,
    };
  }
  if (result.is_partial) {
    return {
      severity: 'warning',
      prefix: 'Partially correct',
      answerLabel: 'Full answer:',
      aiEvaluated: false,
      needsReview: false,
    };
  }
  if (!result.was_answered) {
    return { severity: 'info', prefix: null, answerLabel: 'Correct answer:', aiEvaluated: false, needsReview: false };
  }
  if (result.submitted_drawing) {
    if (!result.ai_evaluated) {
      return {
        severity: 'info',
        prefix: 'Drawing saved ✓',
        answerLabel: 'How to do it:',
        aiEvaluated: false,
        needsReview: false,
      };
    }
    return {
      severity: 'warning',
      prefix: 'Incorrect (AI Vision)',
      answerLabel: 'Correct answer:',
      aiEvaluated: true,
      needsReview: false,
    };
  }
  return { severity: 'error', prefix: 'Incorrect', answerLabel: 'Correct answer:', aiEvaluated: false, needsReview: false };
};

/** Returns true when the answer value is considered empty/unanswered. */
const isAnswerEmpty = (val) => {
  if (val === null || val === undefined || val === '') return true;
  if (Array.isArray(val) && val.every((v) => !v || v === '')) return true;
  if (typeof val === 'object' && !Array.isArray(val)) {
    // Interactive written-companion value { interactive:[...], text }.
    if (Array.isArray(val.interactive) && val.interactive.length > 0) return false;
    // Matching value: a { leftItem: rightItem } map with no text/drawing key of its own — without this
    // every matching answer counted as unanswered in the palette and the "unanswered" warning.
    if (Object.values(val).some((v) => typeof v === 'string' && v.trim() !== '')) return false;
    return (!val.text || val.text.trim() === '') && !val.drawing;
  }
  return false;
};
const groupedSubAnsweredCount = (q, ans) => {
  const map = ans && typeof ans === 'object' && !Array.isArray(ans) ? ans : {};
  const subs = q?.sub_questions || [];
  let answered = 0;
  subs.forEach((sub, idx) => {
    const key = sub.id || `${q.id}_sub_${idx}`;
    if (groupedAnswerHasContent(map[key])) answered += 1;
  });
  return { answered, total: subs.length };
};
const computeQuestionStatus = (q, ans) => {
  if (q?.type === 'grouped' && q?.sub_questions?.length) {
    const { answered, total } = groupedSubAnsweredCount(q, ans);
    if (answered === 0) return STATUS.UNANSWERED;
    return answered >= total ? STATUS.ANSWERED : STATUS.PARTIAL;
  }
  return !isAnswerEmpty(ans) ? STATUS.ANSWERED : STATUS.UNANSWERED;
};
const isPlotPointsQuestion = (question) => {
  if (!question) return false;
  const subtype = String(question?.question_subtype || question?.answer_data?.question_subtype || '').toLowerCase();
  if (subtype.includes('plot')) return true;
  const text = String(question?.question_text || '').toLowerCase();
  return text.includes('plot') && (text.includes('point') || text.includes('axes') || text.includes('coordinate'));
};
const collectQuestionImages = (question) => {
  if (!question) return [];
  const fromArray = Array.isArray(question.image_urls) ? question.image_urls.filter(Boolean) : [];
  const single = !Array.isArray(question.image_urls) && question.image_url ? [question.image_url] : [];
  const base = [...fromArray, ...single];
  const html = String(question?.question_text || '');
  const embedded = [];
  const re = /<img[^>]*\ssrc=["']([^"']+)["']/gi;
  let m;
  while ((m = re.exec(html)) !== null) {
    if (m[1] && !base.includes(m[1])) embedded.push(m[1]);
  }
  return [...base, ...embedded];
};

const isInteractiveDrawing = (question) =>
  String(question?.type || question?.question_type || '').toLowerCase() === 'drawing'
  && !!question?.answer_data?.interactive;

// Which of a question's figures the canvas paints. A question carrying several images (a dice photo
// AND the probability line to mark) has only one drawing surface, and spec.image_url is the only
// place that choice can be expressed — the fallback to the FIRST image is a guess.
const interactiveImageOf = (question) =>
  question?.answer_data?.interactive?.image_url
  || (Array.isArray(question?.image_urls) ? question.image_urls.find(Boolean) : null)
  || question?.image_url
  || '';
const interactiveImgKey = (u) => String(u || '').split('?')[0].split('/').pop();

// Short instruction shown above the interactive canvas — mirrors StudentPaperTake.jsx's
// interactiveHint() so students see the same guidance in mock exams as in past-paper practice.
const interactiveHint = (spec) => {
  if (!spec) return null;
  if (typeof spec.instructions === 'string' && spec.instructions.trim()) return spec.instructions.trim();
  if (spec.kind === 'shade') {
    if (spec.require_labels) {
      return 'Tap each hour section to shade it from the table (tap again to undo). Type the activity name in each label box.';
    }
    if (Number(spec.target_shape_count) > 1) {
      return `Tap squares to shade them in. Draw all ${Number(spec.target_shape_count)} shapes on this one grid${spec.require_rectangles ? ' — each one a solid rectangle' : ''}. Tap again to undo.`;
    }
    return 'Tap the shapes/regions to shade them. Tap again to undo.';
  }
  if (spec.kind === 'rect') return 'Tap two opposite corners on the grid to draw your rectangle.';
  if (spec.kind === 'lines') return 'Tap two points to draw each line of symmetry. Add as many as a shape needs; tap a line to remove it. Leave a shape blank if it has none.';
  if (spec.kind === 'cut-drag') return 'Tap two points to draw your straight cut — the shape splits into two pieces. Then drag a piece to show how they fit together to make the new shape.';
  if (spec.kind === 'segments') return 'Tap one cross then another to join them with a straight line. Tap a line to remove it.';
  if (spec.kind === 'points' || spec.kind === 'drag-shape') {
    const labels = Array.isArray(spec.point_labels) ? spec.point_labels.filter(Boolean) : [];
    const labelReminder = spec.require_point_labels
      ? ' Type the matching letter in the box next to each point — an unlabelled point is not enough.'
      : '';
    const shapeLabelReminder = spec.require_shape_label
      ? ' After the shape is complete, type the letter that names it in the popup — an unlabelled shape is not enough.'
      : '';
    const lines = Number(spec.shape_count) > 1 ? Number(spec.shape_count) : 0;
    const lineReminder = lines
      ? ` Your points are joined as ${lines} separate lines — plot both ends of one line before starting the next.`
      : '';
    const needsJoin = (spec.connect !== false && spec.closed !== false)
      || !!spec.join_context_edges
      || Number(spec.required_edge_count) > 0;
    const joinReminder = needsJoin && !lines
      ? ' Then join them with straight lines: tap one point, then another, to draw each side (or use Connect corners).'
      : '';
    return labels.length
      ? `Tap the grid to plot, in order: ${labels.join(', ')}. Tap a point again to remove it.${lineReminder}${joinReminder}${labelReminder}${shapeLabelReminder}`
      : `Tap the grid intersections to plot your point(s). Tap a point again to remove it.${lineReminder}${joinReminder}${labelReminder}${shapeLabelReminder}`;
  }
  if (spec.kind === 'polygon') {
    const shape = spec.require_shape ? spec.require_shape : 'shape';
    const multiShape = Number(spec.shape_count) > 1 || (Array.isArray(spec.target_areas) && spec.target_areas.length > 1);
    const named = spec.require_point_labels && Array.isArray(spec.point_labels) ? spec.point_labels.filter(Boolean) : [];
    const labelReminder = named.length
      ? ` Name the corners you are asked for (${named.join(', ')}) — an unlabelled corner is not enough. Corners already printed on the diagram need no label.`
      : '';
    if (spec.closed === false || multiShape) return `Tap the grid points to draw your ${shape}. Tap a point again to remove it.${labelReminder}`;
    return `Tap empty grid dots to place the corners of your ${shape}. Then tap one corner and another to draw an edge between them; work round the shape. Tap an edge to remove it, or tap a corner then its × to delete it. Use “Fill in” to colour it.${labelReminder}`;
  }
  if (spec.kind === 'glyph') return 'Tap the diagram to add a letter, then type it. Click any letter to select it — its tools (rotate ⟳ / flip ⇋ ⤢ / remove ✕) appear beside it. Drag a letter, or use the arrow keys, to move it into place. Click elsewhere to close the tools.';
  if (spec.kind === 'reflect') return 'The shape and mirror line are shown. Tap empty grid dots to place the corners of its reflection. Then tap one corner and another to draw an edge between them; work round the shape. Tap an edge to remove it, or tap a corner then its × to delete it. Use “Fill in” to colour the finished shape.';
  if (spec.kind === 'rotate') return 'The shape and rotation centre are shown. Tap empty grid dots to place the corners of the rotated shape. Then tap one corner and another to draw an edge between them; work round the shape. Tap an edge to remove it, or tap a corner then its × to delete it. Use “Fill in” to colour the finished shape.';
  if (spec.kind === 'clock') return 'Drag the short (hour) and long (minute) hands to show the time.';
  if (spec.kind === 'marker') {
    if (Array.isArray(spec.bars) && spec.bars.length > 1) return 'Tap inside each dotted column at the height its bar should reach — read the value off the scale on the vertical axis. Tap a column again to change it.';
    return spec.style === 'bar' ? 'Tap the axis to set the height of the bar.' : 'Tap the number line / scale to mark the value.';
  }
  if (spec.kind === 'pictogram') return 'Tap each icon to fill it in — tap once for a whole, then again for ¾, ½ and ¼, and once more to clear. Complete the row to show the right amount.';
  return null;
};

// The correct-answer value for the reveal, per interactive kind (mirrors the deterministic targets).
const interactiveRevealValue = (spec) => {
  if (!spec) return [];
  if (spec.kind === 'shade') {
    if (Array.isArray(spec.target_regions) && spec.target_regions.length) return spec.target_regions.map(String);
    if (spec.target_mode === 'count' && spec.target_count > 0) {
      // Reveal an example: the first N non-given regions.
      return (spec.regions || []).filter((r) => !r.given).slice(0, spec.target_count).map((r) => String(r.id));
    }
    if (spec.target_mode === 'polyomino' && Array.isArray(spec.target_shape)) {
      const byCell = new Map((spec.regions || []).map((r) => [`${r.cell?.[0]},${r.cell?.[1]}`, String(r.id)]));
      return spec.target_shape.map(([c, r]) => byCell.get(`${c},${r}`)).filter(Boolean);
    }
    return [];
  }
  if (spec.kind === 'lines') return spec.target_lines || [];
  if (spec.kind === 'cut-drag') {
    const s = Array.isArray(spec.shape) ? spec.shape : [];
    if (!Array.isArray(spec.target_cut) || s.length < 4) return null;
    const b = [Number(s[2][0]) - Number(s[3][0]), Number(s[2][1]) - Number(s[3][1])];
    return { cut: spec.target_cut, offsets: [b, [0, 0]] };
  }
  if (spec.kind === 'segments') {
    const nodes = Array.isArray(spec.nodes) ? spec.nodes : [];
    return (spec.target_segments || []).map(([i, j]) => (nodes[i] && nodes[j] ? [nodes[i], nodes[j]] : null)).filter(Boolean);
  }
  if (spec.kind === 'glyph') return spec.targets || [];
  if (spec.kind === 'marker') {
    const axis = spec.axis;
    const hasAxis = Array.isArray(axis) && axis.length >= 2 && Array.isArray(axis[0]) && Array.isArray(axis[1]);
    if (hasAxis && Array.isArray(spec.bars) && spec.bars.length) {
      const v0 = Number(spec.v0 ?? 0), v1 = Number(spec.v1 ?? 1), span = v1 - v0;
      return spec.bars
        .filter((bar) => bar && Number.isFinite(Number(bar.x)) && bar.target_value != null)
        .map((bar) => {
          const t = span === 0 ? 0 : Math.max(0, Math.min(1, (Number(bar.target_value) - v0) / span));
          return [Number(bar.x), axis[0][1] + t * (axis[1][1] - axis[0][1])];
        });
    }
    return spec.target_point ? [spec.target_point] : [];
  }
  if (spec.kind === 'pictogram') {
    const count = Math.max(0, Math.round(Number(spec.row?.count) || 0));
    const t = Number(spec.target_icons) || 0;
    const full = Math.floor(t + 1e-9), frac = Math.round((t - full) * 4) / 4;
    return Array.from({ length: count }, (_, i) => (i < full ? 1 : i === full ? frac : 0));
  }
  if (spec.kind === 'clock') {
    const c = Array.isArray(spec.center) && spec.center.length >= 2 ? spec.center : [0.5, 0.5];
    const r = Number(spec.radius) || 0.4;
    const hour = (Number(spec.target_hour) || 0) % 12, minute = Number(spec.target_minute) || 0;
    const hb = hour * 30 + minute * 0.5, mb = minute * 6;
    const ep = (b, rr) => [c[0] + Math.sin((b * Math.PI) / 180) * rr, c[1] - Math.cos((b * Math.PI) / 180) * rr];
    return [ep(hb, r * 0.55), ep(mb, r * 0.92)];
  }
  return spec.target_points || [];
};

const collectAllQuestionImageEntries = (question) => {
  if (!question) return [];
  const entries = [];
  const seen = new Set();
  const push = (url, label) => { if (url && !seen.has(url)) { seen.add(url); entries.push({ url, label }); } };
  collectQuestionImages(question).forEach((url, i) => push(url, i === 0 ? 'Question diagram' : `Diagram ${i + 1}`));
  (question.sub_questions || []).forEach((sub, idx) => {
    const part = String.fromCharCode(65 + idx);
    collectQuestionImages(sub).forEach((url, i) => push(url, i === 0 ? `Part ${part}` : `Part ${part} image ${i + 1}`));
  });
  return entries;
};


// ---- Render question answer input based on type ----
const QuestionInput = ({ question, answer, onChange, questionIdx, isTestMode, onCheckAnswer, verifyingAnswers, verifyResult, contextImageUrl, questionImages, pendingFloatImage, onFloatImageConsumed, showMathTools = true, showAnswerUnit = true }) => {
  const rawType = question.type || 'multiple-choice';
  // Inline image-fill renders like multi-fill (image lives in the question text; blanks are
  // separate fields, graded positionally). Only box-mode image-fill uses the overlay renderer.
  const type = (rawType === 'image-fill' && question.answer_data?.overlay_layout?.mode === 'inline')
    ? 'multi-fill'
    : rawType;
  const blankInputRefs = useRef({});
  const [activeBlankIdx, setActiveBlankIdx] = useState(0);

  // "Check Answer" button rendered next to TypeDraw tabs or below other inputs
  const checkAnswerBtn = isTestMode ? (
    <Button
      size="small"
      variant="outlined"
      color="info"
      onClick={onCheckAnswer}
      disabled={verifyingAnswers}
      startIcon={verifyingAnswers ? <CircularProgress size={13} color="inherit" /> : null}
      sx={{ fontWeight: 700, textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
    >
      {verifyingAnswers ? 'Checking…' : '✓ Check Answer'}
    </Button>
  ) : null;

  // Inline result shown below any input type — renders LaTeX/HTML in the answer
  const _vdp = getVerifyDisplayProps(verifyResult);
  const resultAlert = isTestMode && verifyResult && _vdp ? (
    <Alert
      severity={_vdp.severity}
      sx={{ mt: 1.5, borderRadius: 2, py: 0.75, fontSize: '0.85rem' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {verifyResult.correct_answer ? (
          <>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              {_vdp.prefix && <span style={{ fontWeight: 700 }}>{_vdp.prefix}</span>}
              {verifyResult.hint && (
                <span style={{ fontStyle: 'italic' }}> — {verifyResult.hint}</span>
              )}
            </Box>
            {isBoxModeImageFillQuestion(question)
              ? renderImageFillCorrectAnswer(question, _vdp.answerLabel, verifyResult.correct_answer)
              : renderCorrectAnswer(verifyResult.correct_answer, _vdp.answerLabel, type === 'essay')}
          </>
        ) : (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            <span style={{ fontWeight: 700 }}>{_vdp.prefix ?? ''}</span>
            {verifyResult.hint && (
              <span style={{ fontStyle: 'italic' }}> — {verifyResult.hint}</span>
            )}
          </Box>
        )}
        {renderCorrectOptionImages(verifyResult, _vdp.answerLabel)}
        {verifyResult.submitted_answer && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, opacity: 0.85 }}>
            <span>Your answer:</span>
            <span style={{ fontStyle: 'italic' }}>{verifyResult.submitted_answer}</span>
          </Box>
        )}
        {verifyResult.submitted_drawing && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
              Your drawing:
            </Typography>
            <Box
              component="img"
              src={verifyResult.submitted_drawing}
              alt="Your handwritten answer"
              sx={{
                maxWidth: '100%',
                maxHeight: 180,
                borderRadius: 1,
                border: '1px solid',
                borderColor: verifyResult.is_correct ? 'success.light' : 'error.light',
                display: 'block',
                bgcolor: 'white',
              }}
            />
            {_vdp.aiEvaluated && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                ✦ Evaluated by AI Vision (GPT-4o)
              </Typography>
            )}
          </Box>
        )}
        {(verifyResult.correct_drawing || verifyResult.correct_solution_image_url) && !verifyResult.correct_interactive && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
              Correct plotted solution:
            </Typography>
            <Box
              component="img"
              src={verifyResult.correct_drawing || verifyResult.correct_solution_image_url}
              alt="Correct plotted solution"
              sx={{
                maxWidth: '100%',
                maxHeight: 220,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.light',
                display: 'block',
                bgcolor: 'white',
              }}
            />
          </Box>
        )}
        {verifyResult.correct_interactive && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
              Correct solution:
            </Typography>
            <InteractiveDiagramCanvas
              spec={verifyResult.correct_interactive}
              imageUrl={verifyResult.correct_interactive.image_url || question.image_url || ''}
              maxWidth={480}
              readOnly
              revealValue={interactiveRevealValue(verifyResult.correct_interactive)}
            />
          </Box>
        )}
      </Box>
    </Alert>
  ) : null;

  if (['multiple-choice', 'true-false'].includes(type)) {
    const options = question.options || [];
    const hasImageOptions = options.some((o) => o.image_url);

    // English error-finding MCQ — bracket visualization + A/B/C/D/N buttons
    if (isErrorFindingStandalone(question) || isErrorFindingContext(question)) {
      return (
        <EnglishErrorFindingLayout
          question={question}
          selected={answer || ''}
          onSelect={onChange}
          showAnswers={false}
          mode="take"
        />
      );
    }

    // English cloze word-choice — inline bordered option boxes
    if (isClozeWordChoice(question)) {
      return (
        <EnglishClozePassageLayout
          question={question}
          selected={answer || ''}
          onSelect={onChange}
          showAnswers={false}
          mode="take"
        />
      );
    }

    // Image-option MCQ — render as clickable 2-column grid of image cards
    if (hasImageOptions) {
      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 2,
            mt: 1,
          }}
        >
          {options.map((opt) => {
            const val = opt.option_text || opt.text || '';
            const selected = answer === val;
            return (
              <Paper
                key={opt.id || val}
                onClick={() => onChange(val)}
                elevation={selected ? 4 : 1}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  borderRadius: 2,
                  textAlign: 'center',
                  border: '2px solid',
                  borderColor: selected ? 'primary.main' : '#e0e0e0',
                  bgcolor: selected ? '#e8f0fe' : 'white',
                  transition: 'all 0.15s',
                  userSelect: 'none',
                  '&:hover': { borderColor: 'primary.light', boxShadow: 3 },
                }}
              >
                {opt.image_url && (
                  <Box sx={{ mb: 0.5 }}>
                    <img
                      src={opt.image_url}
                      alt={val}
                      style={{
                        maxWidth: '100%',
                        maxHeight: 130,
                        objectFit: 'contain',
                        borderRadius: 4,
                        display: 'block',
                        margin: '0 auto',
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'block',
                    fontWeight: selected ? 700 : 400,
                    color: selected ? 'primary.main' : 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  <HtmlWithLatex html={val} />
                </Box>
              </Paper>
            );
          })}
        </Box>
      );
    }

    // Text-option MCQ — existing radio button list
    return (
      <RadioGroup
        value={answer || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <FormControlLabel
            key={opt.id || opt.option_text}
            value={opt.option_text || opt.text || ''}
            control={<Radio />}
            label={<HtmlWithLatex html={opt.option_text || opt.text || ''} />}
            sx={{
              mb: 1,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: answer === (opt.option_text || opt.text) ? 'primary.main' : '#e0e0e0',
              bgcolor: answer === (opt.option_text || opt.text) ? 'primary.50' : 'transparent',
              transition: 'all 0.15s',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          />
        ))}
      </RadioGroup>
    );
  }

  if (type === 'multiple-select') {
    const options = question.options || [];
    const selected = Array.isArray(answer) ? answer : [];
    const hasImageOptions = options.some((o) => o.image_url);
    const toggle = (val) => {
      if (selected.includes(val)) onChange(selected.filter((v) => v !== val));
      else onChange([...selected, val]);
    };

    if (hasImageOptions) {
      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 2,
            mt: 1,
          }}
        >
          {options.map((opt) => {
            const val = opt.option_text || opt.text || '';
            const isSel = selected.includes(val);
            return (
              <Paper
                key={opt.id || val}
                onClick={() => toggle(val)}
                elevation={isSel ? 4 : 1}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  borderRadius: 2,
                  textAlign: 'center',
                  border: '2px solid',
                  borderColor: isSel ? 'primary.main' : '#e0e0e0',
                  bgcolor: isSel ? '#e8f0fe' : 'white',
                  transition: 'all 0.15s',
                  userSelect: 'none',
                  '&:hover': { borderColor: 'primary.light', boxShadow: 3 },
                }}
              >
                {opt.image_url && (
                  <Box sx={{ mb: 0.5 }}>
                    <img
                      src={opt.image_url}
                      alt={val}
                      style={{
                        maxWidth: '100%',
                        maxHeight: 130,
                        objectFit: 'contain',
                        borderRadius: 4,
                        display: 'block',
                        margin: '0 auto',
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'block',
                    fontWeight: isSel ? 700 : 400,
                    color: isSel ? 'primary.main' : 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  <HtmlWithLatex html={val} />
                </Box>
              </Paper>
            );
          })}
        </Box>
      );
    }

    return (
      <Box>
        {options.map((opt) => {
          const val = opt.option_text || opt.text || '';
          return (
            <FormControlLabel
              key={opt.id || val}
              control={
                <Checkbox
                  checked={selected.includes(val)}
                  onChange={() => toggle(val)}
                />
              }
              label={<HtmlWithLatex html={val} />}
              sx={{
                display: 'flex',
                mb: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: selected.includes(val) ? 'primary.main' : '#e0e0e0',
                bgcolor: selected.includes(val) ? 'primary.50' : 'transparent',
                transition: 'all 0.15s',
              }}
            />
          );
        })}
      </Box>
    );
  }

  if (type === 'fill-in-the-blank') {
    return (
      <Box>
        <TypeDrawInput
          answer={answer}
          numericOnly={numericOnlyInput(question)}
          onChange={onChange}
          multiline={false}
          rows={1}
          label="Your Answer"
          questionIdx={questionIdx}
          marks={question.default_marks ?? question.marks ?? question.answer_data?.marks}
          allowDraw={false}
          showMathTools={showMathTools}
          checkAnswerButton={checkAnswerBtn}
          contextImageUrl={contextImageUrl}
          questionImages={questionImages}
          pendingFloatImage={pendingFloatImage}
          onFloatImageConsumed={onFloatImageConsumed}
          {...resolveBlankOrAnswerUnitProps(question, showAnswerUnit, 0)}
        />
        {resultAlert}
      </Box>
    );
  }

  if (type === 'short-answer') {
    const compoundUnits = resolveCompoundUnits(question, showAnswerUnit);

    if (compoundUnits) {
      return (
        <Box>
          <CompoundUnitInput
            units={compoundUnits}
            value={answer}
            onChange={onChange}
            showMathTools={showMathTools}
            trailing={checkAnswerBtn}
          />
          {resultAlert}
        </Box>
      );
    }

    const { unitSuffix, unitPrefix } = resolveAnswerUnitProps(question, showAnswerUnit);
    const ansLen = Number(question.answer_data?.answer_length) || 0;
    const longAnswer = ansLen === 0 || ansLen > 40;
    return (
      <Box>
        <TypeDrawInput
          answer={answer}
          numericOnly={numericOnlyInput(question)}
          onChange={onChange}
          multiline={longAnswer}
          rows={longAnswer ? 4 : undefined}
          answerLength={longAnswer ? undefined : ansLen}
          label="Your Answer"
          questionIdx={questionIdx}
          marks={question.default_marks ?? question.marks ?? question.answer_data?.marks}
          allowDraw
          defaultTab={0}
          measurementMode={showMathTools ? getMeasurementMode(question) : null}
          showMathTools={showMathTools}
          checkAnswerButton={checkAnswerBtn}
          drawLabel="Draw / Write Working"
          contextImageUrl={contextImageUrl}
          questionImages={questionImages}
          pendingFloatImage={pendingFloatImage}
          onFloatImageConsumed={onFloatImageConsumed}
          unitSuffix={unitSuffix}
          unitPrefix={unitPrefix}
        />
        {resultAlert}
      </Box>
    );
  }

  if (type === 'multi-fill') {
    const blanks = question.answer_data?.correct_blanks || question.blanks || [];
    const blankCount = blanks.length > 0 ? blanks.length : Math.max(1, countBlanksInText(question.question_text));
    const answers = Array.isArray(answer)
      ? answer
      : blankCount === 1
        ? [answer ?? '']
        : Array(blankCount).fill('');
    const values = Array.from({ length: blankCount }, (_, i) => answers[i] ?? '');
    const labels = question.answer_data?.blank_labels || [];
    const mathAdapters = blankMathToolsAdapters(
      question,
      activeBlankIdx,
      values[activeBlankIdx] ?? '',
      (newVal) => {
        const next = [...values];
        next[activeBlankIdx] = newVal;
        onChange(blankCount === 1 ? next[0] : next);
      }
    );
    return (
      <Box>
        {showMathTools ? (
          <MathInputTools
            getInput={() => blankInputRefs.current[activeBlankIdx] ?? null}
            getValue={mathAdapters.getValue}
            onChange={mathAdapters.onChange}
          />
        ) : null}
        {values.map((val, idx) => (
          <FillBlankUnitTextField
            key={idx}
            question={question}
            blankIdx={idx}
            fullWidth
            label={labels[idx] ? undefined : `Blank ${idx + 1}`}
            value={val}
            onChange={(next) => {
              const updated = [...values];
              updated[idx] = next;
              onChange(blankCount === 1 ? updated[0] : updated);
            }}
            onFocus={() => setActiveBlankIdx(idx)}
            inputRef={(el) => { blankInputRefs.current[idx] = el; }}
            sx={{ mb: 1.5 }}
            inputProps={labels[idx] ? { 'aria-label': String(labels[idx]) } : undefined}
          />
        ))}
        {checkAnswerBtn && <Box sx={{ mt: 0.5, mb: 1 }}>{checkAnswerBtn}</Box>}
        {resultAlert}
      </Box>
    );
  }

  if (type === 'image-fill') {
    const overlay = question.answer_data?.overlay_layout;
    const boxCount = Array.isArray(overlay?.blanks) ? overlay.blanks.length : 0;
    // Combined part: typed boxes on one image PLUS a drawing/shade answer on a second image (e.g.
    // fill the table cells AND shade the bar for the missing match). Answer = { blanks, interactive }.
    const combinedSpec = question.answer_data?.interactive || null;
    const rawBlanks = combinedSpec
      ? (answer && typeof answer === 'object' && !Array.isArray(answer) ? answer.blanks : answer)
      : answer;
    // interactiveValueOf, not interactiveArrayOf: the canvas re-reads its drawn outline out of
    // value.edges, so the bare point array wipes every edge as soon as it's drawn (and setBlanks below
    // writes this value straight back, which would drop the outline on every keystroke too).
    const combinedInteractive = combinedSpec
      ? interactiveValueOf(answer && typeof answer === 'object' && !Array.isArray(answer) ? answer.interactive : [])
      : [];
    const values = Array.isArray(rawBlanks)
      ? rawBlanks
      : boxCount === 1
        ? [rawBlanks ?? '']
        : Array(boxCount).fill('');
    const setBlanks = (next) => onChange(combinedSpec
      ? { blanks: next, interactive: combinedInteractive }
      : (boxCount === 1 ? next[0] : next));
    return (
      <Box>
        <ImageOverlayFillInput
          overlayLayout={overlay}
          values={values}
          prefilled={question.answer_data?.blank_labels}
          showMathTools={showMathTools}
          onChange={setBlanks}
        />
        {combinedSpec ? (
          <Box sx={{ mt: 2 }}>
            {interactiveHint(combinedSpec) ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                {interactiveHint(combinedSpec)}
              </Typography>
            ) : null}
            <InteractiveDiagramCanvas
              spec={combinedSpec}
              imageUrl={combinedSpec.image_url || question.image_url || ''}
              maxWidth={480}
              value={combinedInteractive}
              onChange={(v) => onChange({ blanks: values, interactive: v })}
            />
          </Box>
        ) : null}
        {checkAnswerBtn}
        {resultAlert}
      </Box>
    );
  }

  if (type === 'drawing') {
    const iSpec = question.answer_data?.interactive;
    if (iSpec) {
      const written = interactiveWrittenOf(iSpec);
      const hint = interactiveHint(iSpec);
      return (
        <Box>
          {hint && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              {hint}
            </Typography>
          )}
          <InteractiveDiagramCanvas
            spec={iSpec}
            imageUrl={interactiveImageOf(question)}
            maxWidth={480}
            value={interactiveValueOf(answer)}
            onChange={(v) => onChange(written ? { interactive: v, text: interactiveWrittenText(answer) } : v)}
          />
          {written ? (
            <Box sx={{ mt: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label={written.prompt || 'Write your answer'}
                value={interactiveWrittenText(answer)}
                onChange={(e) => onChange({ interactive: interactiveValueOf(answer), text: e.target.value })}
              />
            </Box>
          ) : null}
          {checkAnswerBtn ? <Box sx={{ mt: 1 }}>{checkAnswerBtn}</Box> : null}
          {resultAlert}
        </Box>
      );
    }
    return (
      <Box>
        <TypeDrawInput
          answer={answer}
          numericOnly={numericOnlyInput(question)}
          onChange={onChange}
          multiline={false}
          rows={3}
          label="Your Answer"
          questionIdx={questionIdx}
          marks={question.default_marks ?? question.marks ?? question.answer_data?.marks}
          allowDraw
          defaultTab={1}
          measurementMode={showMathTools ? getMeasurementMode(question) : null}
          showMathTools={showMathTools}
          checkAnswerButton={checkAnswerBtn}
          drawLabel="Draw Your Answer"
          contextImageUrl={contextImageUrl}
          questionImages={questionImages}
          pendingFloatImage={pendingFloatImage}
          onFloatImageConsumed={onFloatImageConsumed}
        />
        {resultAlert}
      </Box>
    );
  }

  if (type === 'essay') {
    return (
      <Box>
        <TypeDrawInput
          answer={answer}
          onChange={onChange}
          multiline
          rows={6}
          label="Your Essay Answer"
          questionIdx={questionIdx}
          marks={question.default_marks ?? question.marks ?? question.answer_data?.marks}
          allowDraw={false}
          showMathTools={showMathTools}
          checkAnswerButton={checkAnswerBtn}
          contextImageUrl={contextImageUrl}
          questionImages={questionImages}
          pendingFloatImage={pendingFloatImage}
          onFloatImageConsumed={onFloatImageConsumed}
        />
        {resultAlert}
      </Box>
    );
  }

  if (type === 'matching') {
    const options = question.options || [];
    // The pool comes from the server already de-duplicated and shuffled (match_options). It used to be
    // built from each option's own match_text, which listed the right items in pair order — the correct
    // answer was simply "the Nth item in the dropdown".
    const rightItems = (question.match_options || []).filter((r) => String(r ?? '').trim() !== '');
    const leftItems = options.map((o) => o.option_text || o.text || '');
    if (rightItems.length === 0) return null;
    const selected = typeof answer === 'object' && answer !== null && !Array.isArray(answer) ? answer : {};
    return (
      <Box sx={{ mt: 1 }}>
        {leftItems.map((left, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="body2" sx={{ minWidth: 120 }}><HtmlWithLatex html={left} /></Typography>
            <Select
              size="small"
              value={selected[left] ?? ''}
              onChange={(e) => onChange({ ...selected, [left]: e.target.value })}
              displayEmpty
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Select...</MenuItem>
              {rightItems.map((r) => (
                <MenuItem key={r} value={r}><HtmlWithLatex html={r} /></MenuItem>
              ))}
            </Select>
          </Box>
        ))}
      </Box>
    );
  }

  if (type === 'ordering') {
    const options = question.options || [];
    const items = options.map((o) => o.option_text || o.text || '');
    const order = Array.isArray(answer) ? answer : [];
    const move = (fromIdx, direction) => {
      const next = [...order];
      const toIdx = fromIdx + direction;
      if (toIdx < 0 || toIdx >= next.length) return;
      [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
      onChange(next);
    };
    return (
      <Box sx={{ mt: 1 }}>
        {(order.length ? order : items).map((item, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 24 }}>{idx + 1}.</Typography>
            <Typography variant="body2" sx={{ flex: 1 }}><HtmlWithLatex html={item} /></Typography>
            <Button size="small" onClick={() => move(idx, -1)} disabled={idx === 0}>↑</Button>
            <Button size="small" onClick={() => move(idx, 1)} disabled={idx === (order.length || items.length) - 1}>↓</Button>
          </Box>
        ))}
        {order.length === 0 && items.length > 0 && (
          <Button size="small" onClick={() => onChange([...items])} sx={{ mt: 1 }}>Start ordering</Button>
        )}
      </Box>
    );
  }

  // fallback for unknown types
  return (
    <TextField
      fullWidth
      multiline
      rows={3}
      placeholder="Type your answer here..."
      value={typeof answer === 'object' ? JSON.stringify(answer) : (answer || '')}
      onChange={(e) => onChange(e.target.value)}
      label="Your Answer"
    />
  );
};

/**
 * Re-nest flat backend answers into the frontend's nested format for grouped questions.
 *
 * Autosave and start() both store/return flattened answers:
 *   { 'bank_123_sub_0': 'v0', 'bank_123_sub_1': 'v1' }
 *
 * The frontend answer state uses nested format:
 *   { 'bank_123': { 'bank_123_sub_0': 'v0', 'bank_123_sub_1': 'v1' } }
 *
 * Without re-nesting, grouped question answers are invisible after a tab-close resume.
 */
const renestGroupedAnswers = (flatAnswers, questions) => {
  const nested = { ...flatAnswers };
  questions.forEach((q) => {
    if (q.type !== 'grouped' || !q.sub_questions?.length) return;
    const qId = q.id;
    const group = {};
    let found = false;
    q.sub_questions.forEach((sub, idx) => {
      const subId = sub.id || `${qId}_sub_${idx}`;
      if (subId in nested) {
        group[subId] = nested[subId];
        delete nested[subId];
        found = true;
      }
    });
    if (found) nested[qId] = group;
  });
  return nested;
};

// Detect supplementary booklet: question has a supplementary_booklet_url set
const hasSupplementaryBooklet = (q) => !!q?.supplementary_booklet_url;

// Whole-question standing for a grouped question, from the parts checked so far. Each part's own
// panel only ever reports that part, so "Correct ✓" on a) said nothing about a 4-mark question.
const summarizePartVerdicts = (question, verifyResults) => {
  const parts = question?.sub_questions || [];
  const checked = parts
    .map((_, idx) => verifyResults[`${question.id}_sub_${idx}`])
    .filter((r) => r && r.was_answered !== false && r.is_correct !== null);
  if (checked.length === 0) return null;
  const total = parts.length;
  const correct = checked.filter((r) => r.is_correct).length;
  const partial = checked.filter((r) => !r.is_correct && r.is_partial).length;
  const unchecked = total - checked.length;
  return {
    severity: correct === total ? 'success' : (correct + partial > 0 ? 'warning' : 'error'),
    text: correct === total
      ? `all ${total} part${total === 1 ? '' : 's'} correct ✓`
      : `${correct} of ${total} parts correct${partial ? `, ${partial} partially correct` : ''}.`,
    note: unchecked > 0 ? `${unchecked} part${unchecked === 1 ? '' : 's'} not checked yet — use "Check Answer" on each part.` : null,
  };
};

// ---- Grouped question view — one sub-question at a time with navigator ----
const GroupedQuestion = ({ question, answerMap, onSubAnswerChange, questionIdx, scratchDrawingsRef, passageOpen, onSubIdxChange, pendingFloatImage, onFloatImageConsumed, pendingFloatSubId, showMathTools = true, showAnswerUnit = true, isTestMode = false, onCheckSubAnswer, verifyResults = {}, verifyingAnswers = false }) => {
  const subQuestions = question.sub_questions || [];
  const [currentSubIdx, setCurrentSubIdx] = useState(0);

  const goToSubIdx = (idxOrFn) => {
    setCurrentSubIdx((prev) => {
      const next = typeof idxOrFn === 'function' ? idxOrFn(prev) : idxOrFn;
      onSubIdxChange?.(next);
      return next;
    });
  };
  const safeAnswers =
    answerMap && typeof answerMap === 'object' && !Array.isArray(answerMap)
      ? answerMap
      : {};
  const stemText = cleanStemText(question.question_text || question.group_title || '');
  const hideInlineStem = passageOpen && isComprehensionPassage(question);

  // Clamp currentSubIdx if sub-questions count changes
  const safeSubIdx = Math.min(currentSubIdx, Math.max(0, subQuestions.length - 1));

  const sub = subQuestions[safeSubIdx];
  const subKey = sub ? (sub.id || `sub_${safeSubIdx}`) : null;
  const subAnswer = subKey ? safeAnswers[subKey] : undefined;
  const subLabel = toSubLabel(safeSubIdx, sub);
  const extractLabel = subLabel;
  const subWithCleanText = sub
    ? { ...sub, question_text: extractSubCore(sub.question_text, extractLabel) }
    : null;
  const stemMeasureMode = showMathTools ? getMeasurementMode(question) : null;
  const subMeasureMode = showMathTools && sub ? measurementModeForSub(question, sub) : null;
  const subMeasureImgs = sub ? collectQuestionImages(sub) : [];
  const subMeasureHideImage = Boolean(subMeasureMode && subMeasureImgs.length);
  const activeMeasureMode = subMeasureMode || stemMeasureMode || null;

  const subFillBlanks = (() => {
    if (!sub) return null;
    if (sub.type !== 'multi-fill' && sub.type !== 'fill-in-the-blank' && sub.type !== 'table') return null;
    const fromCorrect = sub.answer_data?.correct_blanks;
    if (Array.isArray(fromCorrect) && fromCorrect.length > 0) return fromCorrect;
    const fromLegacy = sub.blanks;
    if (Array.isArray(fromLegacy) && fromLegacy.length > 0) return fromLegacy;
    const fromLabels = sub.answer_data?.blank_labels;
    if (Array.isArray(fromLabels) && fromLabels.length > 0) return fromLabels.map(() => ['']);
    // Fallback: derive blank count from question text so students can still answer
    // even when correct_blanks was not yet stored in the DB.
    if (sub.type === 'fill-in-the-blank') return [['']];
    const textCount = countBlanksInText(sub.question_text);
    if (textCount > 0) return Array.from({ length: textCount }, () => ['']);
    const tableGrid = sub.answer_data?.table_layout?.grid;
    if (Array.isArray(tableGrid) && tableGrid.length > 0) {
      let maxB = -1;
      tableGrid.forEach((row) => (row || []).forEach((cell) => {
        if (cell && typeof cell.b === 'number' && cell.b > maxB) maxB = cell.b;
      }));
      if (maxB >= 0) return Array.from({ length: maxB + 1 }, () => ['']);
    }
    return null;
  })();
  const isMultiOrFill = !!subFillBlanks;
  const n = subFillBlanks ? subFillBlanks.length : 0;
  const subInputValues = subFillBlanks
    ? (() => {
        const arr = Array.isArray(subAnswer) ? [...subAnswer] : n === 1 ? [subAnswer ?? ''] : [];
        return Array.from({ length: n }, (_, i) => arr[i] ?? '');
      })()
    : undefined;
  const subOnInputChange = subFillBlanks
    ? (inputIdx, value) => {
        const arr = Array.isArray(subAnswer) ? [...subAnswer] : n === 1 ? [subAnswer ?? ''] : [];
        const next = Array.from({ length: n }, (_, i) => (i === inputIdx ? value : arr[i] ?? ''));
        onSubAnswerChange(subKey, n === 1 ? next[0] : next);
      }
    : undefined;

  if (subQuestions.length === 0) return null;

  // Passage error-finding questions (Hippos / A Ghostly Encounter) use the comprehension
  // layout — left passage panel + one sub-question at a time with navigator chips —
  // so students don't have to scroll through a long stack of all sub-questions at once.
  // The admin "extend parts" flag forces the expanded (all-parts-at-once) layout — for plotting /
  // drawing groups that share one grid across parts.
  const extendFlag = !!(question.answer_data?.extend_parts);
  const isComprehension = isComprehensionPassage(question) && !extendFlag;

  // Shared grid image already shown as each interactive sub's canvas — drop it from the parent stem
  // so the diagram isn't painted twice.
  const imgFileKey = (u) => String(u || '').split('?')[0].split('/').pop();
  const subInteractiveImgKeys = new Set(
    subQuestions.filter((s) => isInteractiveDrawing(s)).map((s) => imgFileKey(s?.answer_data?.interactive?.image_url || s?.image_url)).filter(Boolean)
  );

  // ── Shared stem block (used in both layouts) ──────────────────────────────
  const StemBlock = stemText && !hideInlineStem ? (
    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
      <Box sx={{ fontSize: '1rem', lineHeight: 1.7, wordBreak: 'break-word' }}>
        <HtmlWithLatex html={stemText} />
      </Box>
      {(() => {
        const imgs = (
          Array.isArray(question.image_urls) && question.image_urls.filter(Boolean).length > 0
            ? question.image_urls.filter(Boolean)
            : question.image_url ? [question.image_url] : []
        ).filter((src) => !subInteractiveImgKeys.has(imgFileKey(src)));
        return imgs.length > 0 ? (
          <Box sx={{ mt: 1.5 }}>
            <MeasurementWorkspace images={imgs} measurementMode={showMathTools ? getMeasurementMode(question) : null} pxPerCm={getPxPerCm(question)} />
          </Box>
        ) : null;
      })()}
    </Box>
  ) : null;

  // ── MATHS / non-comprehension: show all sub-questions at once ─────────────
  if (!isComprehension) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {StemBlock}
          {subQuestions.map((s, idx) => {
          const sMode = showMathTools ? measurementModeForSub(question, s) : null;
          const sImgs = collectQuestionImages(s);
          const sk = s.id || `sub_${idx}`;
          const sAnswer = safeAnswers[sk];
          const sLabel = toSubLabel(idx, s);
          const sExtractLabel = sLabel;
          const sClean = { ...s, question_text: extractSubCore(s.question_text, sExtractLabel) };
          const sFillBlanks = (() => {
            if (s.type !== 'multi-fill' && s.type !== 'fill-in-the-blank' && s.type !== 'table') return null;
            const fromCorrect = s.answer_data?.correct_blanks;
            if (Array.isArray(fromCorrect) && fromCorrect.length > 0) return fromCorrect;
            const fromLegacy = s.blanks;
            if (Array.isArray(fromLegacy) && fromLegacy.length > 0) return fromLegacy;
            const fromLabels = s.answer_data?.blank_labels;
            if (Array.isArray(fromLabels) && fromLabels.length > 0) return fromLabels.map(() => ['']);
            if (s.type === 'fill-in-the-blank') return [['']];
            const textCount = countBlanksInText(s.question_text);
            if (textCount > 0) return Array.from({ length: textCount }, () => ['']);
            const tableGrid = s.answer_data?.table_layout?.grid;
            if (Array.isArray(tableGrid) && tableGrid.length > 0) {
              let maxB = -1;
              tableGrid.forEach((row) => (row || []).forEach((cell) => {
                if (cell && typeof cell.b === 'number' && cell.b > maxB) maxB = cell.b;
              }));
              if (maxB >= 0) return Array.from({ length: maxB + 1 }, () => ['']);
            }
            return null;
          })();
          const sN = sFillBlanks ? sFillBlanks.length : 0;
          const sInputValues = sFillBlanks
            ? (() => {
                const arr = Array.isArray(sAnswer) ? [...sAnswer] : sN === 1 ? [sAnswer ?? ''] : [];
                return Array.from({ length: sN }, (_, i) => arr[i] ?? '');
              })()
            : undefined;
          const sOnInputChange = sFillBlanks
            ? (inputIdx, value) => {
                const arr = Array.isArray(sAnswer) ? [...sAnswer] : sN === 1 ? [sAnswer ?? ''] : [];
                const next = Array.from({ length: sN }, (_, i) => (i === inputIdx ? value : arr[i] ?? ''));
                onSubAnswerChange(sk, sN === 1 ? next[0] : next);
              }
            : undefined;
          return (
              <Paper key={sk} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                {sImgs.length > 0 && sMode ? (
                  <MeasurementWorkspace images={sImgs} measurementMode={sMode} pxPerCm={getPxPerCm(s, question)} />
                ) : null}
                <QuestionPaperLayout
                  hideBlankMarks
                  hideOptions
                  question={sClean}
                  questionNumber={idx + 1}
                  showAnswers={false}
                  subLabel={sLabel}
                  inputValues={sInputValues}
                  onInputChange={sOnInputChange}
                  hideImage={Boolean(sMode && sImgs.length) || isInteractiveDrawing(s)}
                  showMathTools={showMathTools}
                />
                {sFillBlanks && isTestMode && onCheckSubAnswer && (() => {
                  const subVr = verifyResults[`${question.id}_sub_${idx}`] ?? null;
                  const subDp = subVr ? getVerifyDisplayProps(subVr) : null;
                  return (
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box>
                        <Button
                          variant="outlined"
                          size="small"
                          color="info"
                          onClick={() => onCheckSubAnswer(idx)}
                          disabled={verifyingAnswers}
                          sx={{ fontWeight: 700, textTransform: 'none', fontSize: '0.8rem' }}
                        >
                          {verifyingAnswers ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} /> : null}
                          {verifyingAnswers ? 'Checking…' : '✓ Check answer'}
                        </Button>
                      </Box>
                      {subVr && subDp && (
                        <Alert severity={subDp.severity} sx={{ borderRadius: 2, py: 0.75, fontSize: '0.85rem' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {subVr.correct_answer ? (
                              <>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                  {subDp.prefix && <span style={{ fontWeight: 700 }}>{subDp.prefix}</span>}
                                </Box>
                                {renderCorrectAnswer(subVr.correct_answer, subDp.answerLabel, false)}
                              </>
                            ) : (
                              <span style={{ fontWeight: 700 }}>{subDp.prefix ?? ''}</span>
                            )}
                          </Box>
                        </Alert>
                      )}
                    </Box>
                  );
                })()}
                {!sFillBlanks && (
                  <Box sx={{ mt: 2 }}>
                    <QuestionInput
                      question={s}
                      answer={sAnswer}
                      onChange={(val) => onSubAnswerChange(sk, val)}
                      questionIdx={idx}
                      isTestMode={isTestMode}
                      onCheckAnswer={onCheckSubAnswer ? () => onCheckSubAnswer(idx) : undefined}
                      verifyResult={verifyResults[`${question.id}_sub_${idx}`] ?? null}
                      verifyingAnswers={verifyingAnswers}
                      showMathTools={showMathTools}
                      showAnswerUnit={showAnswerUnit}
                      pendingFloatImage={sk === pendingFloatSubId ? pendingFloatImage : undefined}
                      onFloatImageConsumed={sk === pendingFloatSubId ? onFloatImageConsumed : undefined}
                    />
                  </Box>
                )}
              </Paper>
          );
        })}
          <ScratchPad questionIdx={questionIdx} scratchDrawingsRef={scratchDrawingsRef}
            marks={question?.default_marks ?? question?.marks ?? question?.answer_data?.marks} />
        </Box>
    );
  }

  // ── ENGLISH / comprehension: one sub-question at a time with navigator ────
  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!sub?.hide_shared_context && StemBlock}

      {/* Sub-question navigator chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
          Part:
        </Typography>
        {subQuestions.map((s, idx) => {
          const sk = s.id || `sub_${idx}`;
          const answered = groupedAnswerHasContent(safeAnswers[sk]);
          const isCurrent = idx === safeSubIdx;
          const label = toSubLabel(idx, s);
          return (
            <Box
              key={sk}
              onClick={() => goToSubIdx(idx)}
              sx={{
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '50%',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.78rem',
                bgcolor: answered ? '#4caf50' : '#ef5350',
                color: 'white',
                border: isCurrent ? '3px solid #1a1a1a' : '2px solid transparent',
                boxShadow: isCurrent ? '0 0 0 1px #1a1a1a' : 'none',
                transition: 'all 0.15s',
                '&:hover': { opacity: 0.85, transform: 'scale(1.1)' },
              }}
            >
              {label}
            </Box>
          );
        })}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {subQuestions.filter((s, idx) => groupedAnswerHasContent(safeAnswers[s.id || `sub_${idx}`])).length} / {subQuestions.length} answered
        </Typography>
      </Box>

      {/* Current sub-question */}
      {subWithCleanText && (
        <Paper sx={{ p: 2, borderRadius: 2, border: '2px solid', borderColor: 'primary.light', bgcolor: '#fafafa' }}>
          {subMeasureImgs.length > 0 && subMeasureMode ? (
            <MeasurementWorkspace images={subMeasureImgs} measurementMode={subMeasureMode} pxPerCm={getPxPerCm(subWithCleanText, question)} />
          ) : null}
          <QuestionPaperLayout
            hideBlankMarks
            hideOptions
            question={subWithCleanText}
            questionNumber={safeSubIdx + 1}
            showAnswers={false}
            subLabel={subLabel}
            inputValues={subInputValues}
            onInputChange={subOnInputChange}
            hideImage={subMeasureHideImage || isInteractiveDrawing(sub)}
            showMathTools={showMathTools}
          />
          {!isMultiOrFill && (
            <Box sx={{ mt: 2 }}>
              <QuestionInput
                key={subKey}
                question={sub}
                answer={subAnswer}
                onChange={(val) => onSubAnswerChange(subKey, val)}
                questionIdx={safeSubIdx}
                showMathTools={showMathTools}
                showAnswerUnit={showAnswerUnit}
                pendingFloatImage={subKey === pendingFloatSubId ? pendingFloatImage : undefined}
                onFloatImageConsumed={subKey === pendingFloatSubId ? onFloatImageConsumed : undefined}
              />
            </Box>
          )}
        </Paper>
      )}

      {/* Sub-question prev / next */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button size="small" variant="outlined" startIcon={<ArrowBack />}
          onClick={() => goToSubIdx((i) => Math.max(0, i - 1))}
          disabled={safeSubIdx === 0}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Prev part
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Part {safeSubIdx + 1} of {subQuestions.length}
        </Typography>
        <Button size="small" variant="outlined" endIcon={<ArrowForward />}
          onClick={() => goToSubIdx((i) => Math.min(subQuestions.length - 1, i + 1))}
          disabled={safeSubIdx === subQuestions.length - 1}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Next part
        </Button>
      </Box>

      <ScratchPad questionIdx={questionIdx} scratchDrawingsRef={scratchDrawingsRef}
        marks={question?.default_marks ?? question?.marks ?? question?.answer_data?.marks} />
    </Box>
    </>
  );
};

// ---- Main Component ----
const StudentMockExamTake = () => {
  const { id: examId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [examData, setExamData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [statuses, setStatuses] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [showFinalQuestionDialog, setShowFinalQuestionDialog] = useState(false);
  const [reviewFlagged, setReviewFlagged] = useState(false);
  const [reviewAnswerLater, setReviewAnswerLater] = useState(false);
  const [filterPalette, setFilterPalette] = useState('all');
  const [sessionId, setSessionId] = useState(null);
  const [annotating, setAnnotating] = useState(false);
  const [annotationFloatImage, setAnnotationFloatImage] = useState(null);
  const [annotationFloatSubId, setAnnotationFloatSubId] = useState(null);
  const [showProtractor, setShowProtractor] = useState(false);
  const [showRuler, setShowRuler] = useState(false);
  const [showSetSquare, setShowSetSquare] = useState(false);
  const [showCompass, setShowCompass] = useState(false);
  const [aiScoring, setAiScoring] = useState({ active: false, resultId: null, progress: 0 });
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [mcqNoticeDialog, setMcqNoticeDialog] = useState({ open: false, items: [], message: '' });
  const [timeWarningOpen, setTimeWarningOpen] = useState(false);
  const [timeWarningMessage, setTimeWarningMessage] = useState('');
  const [showTabSwitchWarning, setShowTabSwitchWarning] = useState(false);
  const timeWarningShownRef = useRef(new Set());
  const timerRef = useRef(null);
  const aiPollRef = useRef(null);
  const aiProgressRef = useRef(null);
  const scratchDrawingsRef = useRef({});
  const questionDisplayRef = useRef(null);
  const [questionContextSnapshot, setQuestionContextSnapshot] = useState(null);
  const tabSwitchCountRef = useRef(0);
  const currentIdxRef = useRef(0);
  const autoSubmitTriggeredRef = useRef(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [passageOpen, setPassageOpen] = useState(false);
  const [supplementaryOpen, setSupplementaryOpen] = useState(false);
  const [paused, setPaused] = useState(() => location.state?.isPausedResume === true);
  const { isTestingMode } = useExamMode();
  const [sessionIsTestMode, setSessionIsTestMode] = useState(null);
  // Priority: attempt's own is_test_mode (from DB, survives refresh) > nav state > live hook
  const isTestMode =
    sessionIsTestMode !== null
      ? sessionIsTestMode
      : (location.state?.isTestMode ?? isTestingMode('mock_exams'));
  const [verifyResults, setVerifyResults] = useState({});
  const [verifyingAnswers, setVerifyingAnswers] = useState(false);
  const [groupedSubIdx, setGroupedSubIdx] = useState(0); // tracks active sub-question for passage highlighting
  const isTesterMockExam = isTesterUser();
  const canVerifyMockQuestion = isTesterMockExam && testerHasPermission("verify-question");
  const canVerifyMockExam = isTesterMockExam && testerHasPermission("verify-mock_exam");
  const { loadVerifications: loadMockVerifications, submitVerification: submitMockVerification, getItemData: getMockItemData, submitting: mockVerifySubmitting } = useContentVerification();
  const [mockVerifyDialogOpen, setMockVerifyDialogOpen] = useState(false);
  const [mockVerifyQuestionFull, setMockVerifyQuestionFull] = useState(null);
  const [mockVerifyQuestionLoading, setMockVerifyQuestionLoading] = useState(false);
  const [verifyExamDialogOpen, setVerifyExamDialogOpen] = useState(false);
  const [verifyExamSubmitting, setVerifyExamSubmitting] = useState(false);
  // A sign-off only covers the platforms the tester actually reviewed on.
  const [verifyExamPlatforms, setVerifyExamPlatforms] = useState(['web']);
  const [editFromVerifyOpen, setEditFromVerifyOpen] = useState(false);
  const [editFromVerifyQuestion, setEditFromVerifyQuestion] = useState(null);
  const [editFromVerifySaving, setEditFromVerifySaving] = useState(false);
  const pendingNavRef = useRef(null);   // destination path stored when navigation is intercepted
  const isLeavingRef = useRef(false);   // bypass flag so confirmed navigation isn't re-intercepted
  const prevCurrentIdxRef = useRef(-1); // tracks previous question index to distinguish navigation from question-data updates
  const [showAnswerUnit, setShowAnswerUnit] = useState(true);

  // Keep currentIdxRef in sync so goToQuestion always reads the latest index
  currentIdxRef.current = currentIdx;

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  useEffect(() => {
    api.get('/student/settings')
      .then((res) => {
        setShowAnswerUnit(res.data?.data?.show_answer_unit !== false);
        setCustomAnswerUnits(res.data?.data?.answer_units);
      })
      .catch(() => {});
  }, []);

  // Reset passage panel and sub-index when navigating to a different question.
  // groupedSubIdx is only reset when the main question index changes (navigation),
  // not when question data updates (e.g. tester edits a question via the edit dialog).
  useEffect(() => {
    const currentQ = questions[currentIdx];
    const shouldOpen = isComprehensionPassage(currentQ);
    setPassageOpen(shouldOpen);
    setSupplementaryOpen(false);
    if (prevCurrentIdxRef.current !== currentIdx) {
      setGroupedSubIdx(0);
      prevCurrentIdxRef.current = currentIdx;
    }
    setAnnotationFloatImage(null);
    setAnnotationFloatSubId(null);
  }, [currentIdx, questions]);

  // Capture the rendered question display as a data URL for drawing pad context
  useEffect(() => {
    setQuestionContextSnapshot(null);
    const el = questionDisplayRef.current;
    if (!el) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(el, { scale: 1, useCORS: true, logging: false, backgroundColor: '#ffffff' });
        if (!cancelled) setQuestionContextSnapshot(canvas.toDataURL('image/jpeg', 0.9));
      } catch (_) { /* capture failed — drawing pad saves strokes only */ }
    }, 150);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [currentIdx]);

  // ---- Block browser close / tab close / refresh while exam is active ----
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (loading || submitting || aiScoring.active) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [loading, submitting, aiScoring.active]);

  // ---- Intercept ALL in-app navigation (sidebar, tabs, back button) while exam is active ----
  useEffect(() => {
    if (loading || submitting || aiScoring.active) return;

    const origPushState = window.history.pushState.bind(window.history);

    window.history.pushState = (state, title, url) => {
      // Let it through if we triggered the navigation after user confirmed "Leave"
      if (isLeavingRef.current) {
        isLeavingRef.current = false;
        return origPushState(state, title, url);
      }
      if (!url) return origPushState(state, title, url);

      let newPath;
      try { newPath = new URL(String(url), window.location.origin).pathname; }
      catch { newPath = String(url); }

      // Same page — allow (e.g. sentinel pushes, hash changes)
      if (newPath === window.location.pathname) return origPushState(state, title, url);

      // Different page — intercept and show dialog
      pendingNavRef.current = newPath;
      setLeaveDialogOpen(true);
    };

    // Back button: re-push sentinel so popstate fires instead of actually leaving
    origPushState(null, '', window.location.href);
    const handlePopState = () => {
      origPushState(null, '', window.location.href);
      pendingNavRef.current = null; // back button → just show dialog, "Leave" goes home
      setLeaveDialogOpen(true);
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.history.pushState = origPushState;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [loading, submitting, aiScoring.active]);

  // ---- Keyboard shortcuts for easy student access ----
  useEffect(() => {
    if (loading || submitting || aiScoring.active) return;

    const handleKeyDown = (e) => {
      // Ignore if user is typing in an input field
      const target = e.target;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (isInput) return;

      // P key: Toggle passage panel (for comprehension questions)
      if (e.key === 'p' || e.key === 'P') {
        const currentQ = questions[currentIdx];
        if (isComprehensionPassage(currentQ)) {
          setPassageOpen((prev) => !prev);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [loading, submitting, aiScoring.active, currentIdx, questions]);

  // ---- Load exam ----
  useEffect(() => {
    const sessionFromNav = location.state?.session;
    const startFresh = location.state?.startFresh === true;
    const loadExam = async () => {
      setLoading(true);
      try {
        let data;
        if (sessionFromNav) {
          data = sessionFromNav;
        } else {
          const res = await api.get(`/student/mock-exam/${examId}/session`);
          data = res.data?.data;
        }
        const exam = data?.exam || data;
        const qs = exam?.questions || data?.questions || [];
        setExamData(exam);
        setSessionId(data?.session_id || data?.id);
        setQuestions(qs);

        if (data?.first_open) {
          const flaggedForMcq = [];
          qs.forEach((q, idx) => {
            const qNum = String(q.paper_number || idx + 1).trim();
            if (q.mcq_recommended) flaggedForMcq.push({ label: `Q${qNum}`, note: q.mcq_recommended_note });
            (q.sub_questions || []).forEach((sub, subIdx) => {
              if (!sub.mcq_recommended) return;
              // toSubLabel yields "c)" (or the authored paper_number, which may repeat the
              // parent number as in "6c") — reduce it to the bare part letter for the notice.
              let part = toSubLabel(subIdx, sub).replace(/[).\s]+$/, '');
              if (part.startsWith(qNum)) part = part.slice(qNum.length).replace(/^[).\s]+/, '');
              flaggedForMcq.push({
                label: part ? `Q${qNum} — part ${part}` : `Q${qNum}`,
                note: sub.mcq_recommended_note,
              });
            });
          });
          if (flaggedForMcq.length > 0) {
            setMcqNoticeDialog({ open: true, items: flaggedForMcq, message: data?.mcq_notice_message || '' });
          }
        }

        // Resolve test mode from the attempt's own flag (present in both start() and getSession() responses)
        if (data?.is_test_mode != null) {
          setSessionIsTestMode(Boolean(data.is_test_mode));
        }

        // Load existing flagged questions for this exam
        try {
          const flagRes = await api.get(`/student/mock-exam/${examId}/flagged-questions`);
          const rawFlaggedIds = flagRes.data?.data?.flagged_question_ids || [];
          const toNumeric = (id) => String(id).replace(/^bank_/, '');
          const flaggedNums = new Set(rawFlaggedIds.map(toNumeric));
          const initialFlagged = new Set();
          qs.forEach((q, idx) => {
            if (flaggedNums.has(toNumeric(q.id))) {
              initialFlagged.add(idx);
            }
          });
          setFlagged(initialFlagged);
        } catch (err) {
          console.error('Failed to load flagged questions:', err);
        }

        const duration = exam?.duration_minutes || 60;
        // Use remaining_seconds from getSession when available (tab closed/reopened)
        const remainingSeconds = data?.remaining_seconds;
        const hasRemainingTime = typeof remainingSeconds === 'number' && remainingSeconds > 0;
        setTimeLeft(hasRemainingTime ? remainingSeconds : duration * 60);

        // Start Fresh: clear storage and reset all state (user chose Start Fresh from list page)
        if (startFresh) {
          sessionStorage.removeItem(`examAnswers_${examId}`);
          sessionStorage.removeItem(`examQuestion_${examId}`);
          sessionStorage.removeItem(`examScratch_${examId}`);
          sessionStorage.removeItem(`examFlagged_${examId}`);
          sessionStorage.removeItem(`examPaused_${examId}`);
          const initStatuses = {};
          qs.forEach((_, i) => { initStatuses[i] = STATUS.NOT_VISITED; });
          setStatuses(initStatuses);
          setCurrentIdx(0);
          setAnswers({});
          setFlagged(new Set());
          scratchDrawingsRef.current = {};
          return; // finally block sets loading=false
        }

        // Restore from sessionStorage first, then from backend saved_answers (when tab was closed)
        // Note: Resume popup is now in StudentMockExams.jsx — user already chose Resume before navigating
        const savedAnswers = sessionStorage.getItem(`examAnswers_${examId}`);
        const savedIdx = sessionStorage.getItem(`examQuestion_${examId}`);
        const savedScratch = sessionStorage.getItem(`examScratch_${examId}`);
        const backendSavedAnswers = data?.saved_answers;
        const hasBackendAnswers = backendSavedAnswers && typeof backendSavedAnswers === 'object' && Object.keys(backendSavedAnswers).length > 0;
        let restored = false;
        if (savedAnswers && qs.length > 0) {
          try {
            const parsed = JSON.parse(savedAnswers);
            setAnswers(parsed);
            if (savedIdx != null) {
              const idx = parseInt(savedIdx, 10);
              if (!isNaN(idx) && idx >= 0 && idx < qs.length) {
                setCurrentIdx(idx);
              }
            }
            if (savedScratch) {
              try {
                const scratch = JSON.parse(savedScratch);
                if (scratch && typeof scratch === 'object') {
                  scratchDrawingsRef.current = { ...scratch };
                }
              } catch {
                // ignore scratch parse errors
              }
            }
            // Note: flagged state is always loaded from backend API (source of truth),
            // not from sessionStorage, so admin unflag is reflected immediately.

            // Derive statuses from restored answers so palette shows correct colors
            const derivedStatuses = {};
            qs.forEach((q, i) => {
              derivedStatuses[i] = computeQuestionStatus(q, parsed[q.id]);
            });
            setStatuses(derivedStatuses);
            restored = true;
          } catch {
            // ignore parse errors
          }
        }

        // Fallback: restore from backend saved_answers when sessionStorage is empty (e.g. tab was closed).
        // Backend answers are FLAT (bank_X_sub_0 at root); re-nest them for grouped questions.
        if (!restored && hasBackendAnswers && qs.length > 0) {
          try {
            const nestedAnswers = renestGroupedAnswers(backendSavedAnswers, qs);
            setAnswers(nestedAnswers);
            const derivedStatuses = {};
            qs.forEach((q, i) => {
              derivedStatuses[i] = computeQuestionStatus(q, nestedAnswers[q.id]);
            });
            setStatuses(derivedStatuses);
            restored = true;
          } catch {
            // ignore parse errors
          }
        }

        if (!restored) {
          const initStatuses = {};
          qs.forEach((_, i) => { initStatuses[i] = STATUS.NOT_VISITED; });
          setStatuses(initStatuses);
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load exam');
        navigate('/student/mock-exams');
      } finally {
        setLoading(false);
      }
    };
    loadExam();
  }, [examId, location.state, navigate]);

  // ---- Submit (defined early so timer effect can use it) ----
  const handleSubmit = useCallback(
    async (autoSubmit = false) => {
      if (!autoSubmit) setConfirmSubmit(false);
      setSubmitting(true);
      clearInterval(timerRef.current);
      try {
        // Flatten grouped answers: backend expects bank_X_sub_0, bank_X_sub_1
        // We store answers[bank_X] = { bank_X_sub_0: v0, bank_X_sub_1: v1 }
        const flattenedAnswers = { ...answers };
        questions.forEach((q) => {
          const qId = q.id;
          const val = flattenedAnswers[qId];
          if (
            q.type === 'grouped' &&
            q.sub_questions?.length &&
            val != null &&
            typeof val === 'object' &&
            !Array.isArray(val) &&
            val.text == null
          ) {
            Object.assign(flattenedAnswers, val);
            delete flattenedAnswers[qId];
          }
        });

        // Compress drawings before submit so the backend receives the same image size
        // as Check Answer (1200px), enabling cache hits on gradeDrawingWithCache.
        const compressedAnswers = {};
        for (const [key, val] of Object.entries(flattenedAnswers)) {
          if (val && typeof val === 'object' && val.drawing && String(val.drawing).startsWith('data:image/')) {
            compressedAnswers[key] = await compressAnswerForSubmit(val, 1200);
          } else {
            compressedAnswers[key] = val;
          }
        }

        const payload = {
          answers: compressedAnswers,
          session_id: sessionId,
          time_taken: (examData?.duration_minutes || 60) * 60 - timeLeft,
          tab_switches: tabSwitchCountRef.current,
        };
        const res = await api.post(`/student/mock-exam/${examId}/submit`, payload);
        // Clear session storage on successful submit
        sessionStorage.removeItem(`examAnswers_${examId}`);
        sessionStorage.removeItem(`examQuestion_${examId}`);
        sessionStorage.removeItem(`examScratch_${examId}`);
        sessionStorage.removeItem(`examFlagged_${examId}`);
        sessionStorage.removeItem(`examPaused_${examId}`);
        const resultId = res.data?.data?.result_id ?? res.data?.data?.id;
        toast.success('Exam submitted! AI is now scoring your answers...');
        if (resultId != null) {
          setAiScoring({ active: true, resultId, progress: 5 });
        } else {
          navigate('/student/mock-exams', { state: { submitted: true, examId } });
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to submit exam');
      } finally {
        setSubmitting(false);
      }
    },
    [answers, sessionId, examId, examData, timeLeft, navigate, questions]
  );

  // ---- Persist pause state so the exam list card can reflect it ----
  useEffect(() => {
    if (!examId) return;
    if (paused) {
      sessionStorage.setItem(`examPaused_${examId}`, '1');
    } else {
      sessionStorage.removeItem(`examPaused_${examId}`);
    }
  }, [examId, paused]);

  // ---- Session storage persistence (save on every change) ----
  useEffect(() => {
    if (!examId || loading || !examData) return;
    sessionStorage.setItem(`examAnswers_${examId}`, JSON.stringify(answers));
    sessionStorage.setItem(`examQuestion_${examId}`, String(currentIdx));
    // Save flagged state
    sessionStorage.setItem(`examFlagged_${examId}`, JSON.stringify([...flagged]));
    const scratch = scratchDrawingsRef?.current;
    if (scratch && typeof scratch === 'object') {
      const hasAny = Object.values(scratch).some(Boolean);
      if (hasAny) {
        sessionStorage.setItem(`examScratch_${examId}`, JSON.stringify(scratch));
      } else {
        sessionStorage.removeItem(`examScratch_${examId}`);
      }
    }
  }, [examId, examData, loading, answers, currentIdx, flagged]);

  // ---- Anti-cheat: tab/window visibility change only (blur removed — fires too aggressively) ----
  useEffect(() => {
    if (!examData || loading || submitting) return;
    const autoSubmitEnabled = examData.tab_switch_auto_submit_enabled === true;
    const threshold = Math.max(1, examData.tab_switch_auto_submit_threshold ?? 3);
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;
      tabSwitchCountRef.current += 1;
      setShowTabSwitchWarning(true);
      if (autoSubmitEnabled && tabSwitchCountRef.current >= threshold && !autoSubmitTriggeredRef.current) {
        autoSubmitTriggeredRef.current = true;
        toast.warning('Exam auto-submitted due to repeated tab switching.');
        handleSubmit(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [examData, loading, submitting, handleSubmit]);

  // ---- Auto-save every 30 seconds ----
  useEffect(() => {
    if (!examData || loading || !sessionId || submitting) return;
    const flattenAnswers = () => {
      const flat = { ...answers };
      questions.forEach((q) => {
        const qId = q.id;
        const val = flat[qId];
        if (
          q.type === 'grouped' &&
          q.sub_questions?.length &&
          val != null &&
          typeof val === 'object' &&
          !Array.isArray(val) &&
          val.text == null  // ensure it's not a TypeDrawInput object
        ) {
          Object.assign(flat, val);
          delete flat[qId];
        }
      });
      return flat;
    };
    const interval = setInterval(async () => {
      try {
        await api.post(`/student/mock-exam/${examId}/autosave`, {
          answers: flattenAnswers(),
          session_id: sessionId,
        });
        toast.info('Saved', { autoClose: 1500, toastId: 'autosave' });
      } catch {
        // Silently ignore autosave errors
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [examData, loading, sessionId, submitting, answers, questions, examId]);

  // ---- Poll server every 30s to sync admin-cleared flags ----
  useEffect(() => {
    if (loading || submitting) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/student/mock-exam/${examId}/flagged-questions`);
        const _toNum = (id) => String(id).replace(/^bank_/, '');
        const serverIds = new Set((res.data?.data?.flagged_question_ids || []).map(_toNum));
        setFlagged((prev) => {
          const next = new Set(prev);
          questions.forEach((q, idx) => {
            if (prev.has(idx) && !serverIds.has(_toNum(q.id))) next.delete(idx);
          });
          return next;
        });
        setStatuses((prev) => {
          const next = { ...prev };
          questions.forEach((q, idx) => {
            if (next[idx] === STATUS.FLAGGED && !serverIds.has(_toNum(q.id))) {
              next[idx] = computeQuestionStatus(q, answers[q.id]);
            }
          });
          return next;
        });
      } catch { /* non-critical */ }
    }, 30_000);
    return () => clearInterval(interval);
  }, [loading, submitting, examId, questions, answers]);

  // ---- Timer (stops when paused, and while the convert-to-MCQ notice blocks the exam) ----
  useEffect(() => {
    if (!examData || loading || paused || mcqNoticeDialog.open) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit(true);
          return 0;
        }
        // Time warnings at 10 min and 5 min (show each once)
        if (prev === 600 && !timeWarningShownRef.current.has(600)) {
          timeWarningShownRef.current.add(600);
          setTimeWarningMessage('10 minutes remaining. Please review your answers.');
          setTimeWarningOpen(true);
        } else if (prev === 300 && !timeWarningShownRef.current.has(300)) {
          timeWarningShownRef.current.add(300);
          setTimeWarningMessage('5 minutes remaining! Consider submitting soon.');
          setTimeWarningOpen(true);
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [examData, loading, handleSubmit, paused, mcqNoticeDialog.open]);

  // ---- AI Scoring polling (runs after submit, drives progress bar) ----
  useEffect(() => {
    if (!aiScoring.active || !aiScoring.resultId) return;

    const MAX_WAIT_MS = 150_000; // 2.5-minute hard timeout (jobs average 1–2 min)
    const POLL_MS = 2_500;
    let elapsed = 0;

    // Animate progress slowly so it reaches ~88% over ~2 minutes, matching the
    // real job duration (observed 1m 16s – 1m 49s).  Rate: +0.7% every 1000ms
    // → 83% span covered in ≈ 119 s, keeping the bar moving throughout the wait.
    aiProgressRef.current = setInterval(() => {
      setAiScoring((prev) => ({ ...prev, progress: Math.min(prev.progress + 0.7, 88) }));
    }, 1_000);

    aiPollRef.current = setInterval(async () => {
      elapsed += POLL_MS;
      try {
        const res = await api.get(`/student/mock-exam/attempt/${aiScoring.resultId}/ai-feedback`);
        const status = res.data?.data?.status;

        if (status === 'completed') {
          clearInterval(aiPollRef.current);
          clearInterval(aiProgressRef.current);
          setAiScoring((prev) => ({ ...prev, progress: 100 }));
          // Brief pause so user sees 100%, then navigate
          setTimeout(() => {
            navigate(`/student/mock-exam/result/${aiScoring.resultId}/report`, {
              state: { submitted: true, examId, aiCompleted: true, aiScore: res.data?.data?.ai_score },
            });
          }, 700);
        } else if (status === 'failed' || elapsed >= MAX_WAIT_MS) {
          clearInterval(aiPollRef.current);
          clearInterval(aiProgressRef.current);
          if (status === 'failed') {
            toast.warning('AI scoring could not be completed. Your rule-based score has been saved.');
          } else {
            toast.info('AI scoring is taking longer than expected. Showing your score now.');
          }
          navigate(`/student/mock-exam/result/${aiScoring.resultId}/report`, {
            state: { submitted: true, examId },
          });
        }
      } catch {
        // Silently ignore transient network errors during polling
      }
    }, POLL_MS);

    return () => {
      clearInterval(aiPollRef.current);
      clearInterval(aiProgressRef.current);
    };
  }, [aiScoring.active, aiScoring.resultId, examId, navigate]);

  // ---- Navigate to question ----
  const goToQuestion = useCallback(
    (idx) => {
      const prevIdx = currentIdxRef.current;
      // Update the leaving question's status (NOT_VISITED → UNANSWERED) before moving
      setStatuses((prev) => ({
        ...prev,
        [prevIdx]: prev[prevIdx] === STATUS.NOT_VISITED ? STATUS.UNANSWERED : prev[prevIdx],
      }));
      currentIdxRef.current = idx;
      setCurrentIdx(idx);
    },
    []
  );

  // ---- Answer a question ----
  const handleAnswer = (value) => {
    const qId = questions[currentIdx]?.id || currentIdx;
    setAnswers((prev) => ({ ...prev, [qId]: value }));
    const hasContent = (() => {
      if (value == null || value === '') return false;
      if (typeof value === 'object' && !Array.isArray(value)) {
        return String(value.text ?? '').trim() !== '' || !!value.drawing;
      }
      if (Array.isArray(value)) return value.some((v) => v != null && String(v).trim() !== '');
      return String(value).trim() !== '';
    })();
    setStatuses((prev) => {
      const cur = prev[currentIdx];
      return {
        ...prev,
        [currentIdx]: flagged.has(currentIdx)
          ? STATUS.FLAGGED
          : hasContent
          ? STATUS.ANSWERED
          : cur === STATUS.ANSWERED
          ? STATUS.UNANSWERED
          : cur,
      };
    });
  };

  // ---- Answer a sub-question within a grouped stem ----
  const handleGroupedSubAnswer = (subKey, value) => {
    const qId = questions[currentIdx]?.id || currentIdx;

    setAnswers((prev) => {
      const existing = prev[qId];
      const prevGroup =
        existing && typeof existing === 'object' && !Array.isArray(existing)
          ? existing
          : {};
      const nextGroup = { ...prevGroup, [subKey]: value };

      setStatuses((prevStatuses) => {
        return {
          ...prevStatuses,
          [currentIdx]: flagged.has(currentIdx)
            ? STATUS.FLAGGED
            : computeQuestionStatus(questions[currentIdx], nextGroup),
        };
      });

      return {
        ...prev,
        [qId]: nextGroup,
      };
    });
  };

  // ---- Flag / unflag ----
  const [flaggingInProgress, setFlaggingInProgress] = useState({});
  const EMPTY_FLAG_DIALOG = { open: false, reason: '', issue_question: false, issue_answer: false, issue_image: false };
  const [flagReasonDialog, setFlagReasonDialog] = useState(EMPTY_FLAG_DIALOG);

  const toggleFlag = async (dialogPayload = null) => {
    const question = questions[currentIdx];
    if (!question) return;

    const questionId = question.id;
    const isCurrentlyFlagged = flagged.has(currentIdx);

    // When flagging (not unflagging), show dialog first unless payload already provided from dialog Save
    if (!isCurrentlyFlagged && dialogPayload === null) {
      setFlagReasonDialog({ ...EMPTY_FLAG_DIALOG, open: true });
      return;
    }

    // Call API to flag/unflag
    setFlaggingInProgress(prev => ({ ...prev, [currentIdx]: true }));
    try {
      if (isCurrentlyFlagged) {
        // Unflag
        await api.delete(`/student/mock-exam/${examId}/questions/${questionId}/flag`);
      } else {
        // Flag with optional reason and issue types
        await api.post(`/student/mock-exam/${examId}/questions/${questionId}/flag`, {
          reason: dialogPayload?.reason || '',
          issue_question: dialogPayload?.issue_question || false,
          issue_answer: dialogPayload?.issue_answer || false,
          issue_image: dialogPayload?.issue_image || false,
        });
      }
    } catch (err) {
      console.error('Failed to toggle flag:', err);
      toast.error(err.response?.data?.message || 'Failed to flag question');
      if (!isCurrentlyFlagged) setFlagReasonDialog(EMPTY_FLAG_DIALOG);
      setFlaggingInProgress(prev => ({ ...prev, [currentIdx]: false }));
      return;
    } finally {
      if (!isCurrentlyFlagged) setFlagReasonDialog(EMPTY_FLAG_DIALOG);
      setFlaggingInProgress(prev => ({ ...prev, [currentIdx]: false }));
    }

    // Update local state - calculate new flag state first
    setFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(currentIdx)) next.delete(currentIdx);
      else next.add(currentIdx);
      return next;
    });
    // Update statuses with the NEW flag state
    setStatuses((prev) => {
      const willBeFlagged = !flagged.has(currentIdx);
      return {
        ...prev,
        [currentIdx]: willBeFlagged
          ? STATUS.FLAGGED
          : computeQuestionStatus(questions[currentIdx], answers[questions[currentIdx]?.id || currentIdx]),
      };
    });
  };

  const handleFlagReasonSave = () => {
    toggleFlag({
      reason: flagReasonDialog.reason || '',
      issue_question: flagReasonDialog.issue_question,
      issue_answer: flagReasonDialog.issue_answer,
      issue_image: flagReasonDialog.issue_image,
    });
  };

  // ---- Answer later ----
  const handleAnswerLater = () => {
    setStatuses((prev) => ({ ...prev, [currentIdx]: STATUS.ANSWER_LATER }));
    if (currentIdx < questions.length - 1) goToQuestion(currentIdx + 1);
  };

  // compressDrawingForVerify / compositeWithContext imported from answerDrawingUtils

  const compositeAndCompress = async (val, question) => {
    if (val && typeof val === 'object' && val.drawing && String(val.drawing).startsWith('data:image/')) {
      const skipCtx = skipContextForDrawing(question);
      const rawDrawing = val.drawing;
      const withCtx = (!val.diagramOnCanvas && questionContextSnapshot && !skipCtx)
        ? await compositeWithContext(rawDrawing, questionContextSnapshot)
        : rawDrawing;
      const compressed = await compressDrawingForVerify(withCtx, 1200);
      return { ...val, drawing: compressed };
    }
    if (val && typeof val === 'object' && val.drawing) {
      const compressed = await compressDrawingForVerify(val.drawing);
      return { ...val, drawing: compressed };
    }
    return val;
  };

  // ---- Verify single question answer (test mode only) ----
  const handleVerifySingleQuestion = async (subIdxOverride = null) => {
    if (!isTestMode) return;
    const q = questions[currentIdx];
    if (!q) return;
    setVerifyingAnswers(true);
    try {
      let singleAnswerMap = {};
      let wasAnswered = false;

      if (q.type === 'grouped') {
        const subIdx = subIdxOverride ?? groupedSubIdx;
        const subKey = `${q.id}_sub_${subIdx}`;
        const sub = (q.sub_questions || [])[subIdx];
        const groupedVal = answers[q.id];
        const subVal = (groupedVal && typeof groupedVal === 'object' && !Array.isArray(groupedVal) && groupedVal.text == null)
          ? (groupedVal[subKey] ?? null)
          : null;
        wasAnswered = !isAnswerEmpty(subVal);
        singleAnswerMap[subKey] = await compositeAndCompress(subVal, sub);
      } else {
        const val = answers[q.id] ?? null;
        wasAnswered = !isAnswerEmpty(val);
        singleAnswerMap[String(q.id)] = await compositeAndCompress(val, q);
      }

      const res = await api.post(`/student/mock-exam/${examId}/verify-answers`, { answers: singleAnswerMap });
      if (res.data?.success) {
        const map = res.data.data?.results_map || {};
        const qForMeasure = q.type === 'grouped' ? (q.sub_questions || [])[groupedSubIdx] : q;
        const parentForMeasure = q.type === 'grouped' ? q : null;
        const measureMode = parentForMeasure
          ? measurementModeForSub(parentForMeasure, qForMeasure)
          : getMeasurementMode(qForMeasure);
        const submittedText = (() => {
          const val = q.type === 'grouped'
            ? ((answers[q.id] || {})[`${q.id}_sub_${groupedSubIdx}`])
            : answers[q.id];
          if (typeof val === 'string') return val.trim();
          if (val && typeof val === 'object') return String(val.text ?? '').trim();
          return '';
        })();
        // Multi-blank fills are graded positionally by the backend (blank i ↔ correct[i]).
        // The order-independent client-side upgrades below would wrongly accept swapped answers
        // (e.g. First/Second Pattern in the wrong boxes), so trust the backend for these.
        const blankCountFor = (qq) => {
          if (!qq) return 0;
          if (Array.isArray(qq.answer_data?.correct_blanks)) return qq.answer_data.correct_blanks.length;
          if (Array.isArray(qq.answer_data?.blank_labels)) return qq.answer_data.blank_labels.length;
          if (Array.isArray(qq.blanks)) return qq.blanks.length;
          return countBlanksInText(qq.question_text);
        };
        const isImageFillQ = (qq) => String(qq?.type || qq?.question_type || '').toLowerCase().replace(/_/g, '-') === 'image-fill';
        const isMultiBlankFill = (isFillQuestionType(qForMeasure || q) || isImageFillQ(qForMeasure || q)) && blankCountFor(qForMeasure || q) > 1;

        const augmented = {};
        for (const [key, result] of Object.entries(map)) {
          let next = { ...result, was_answered: wasAnswered };
          const hasDrawing = !!next.submitted_drawing;

          // Handle backend returning null is_correct (no AI evaluation performed for this question type)
          if (next.is_correct === null) {
            if (hasDrawing) {
              next = { ...next, is_correct: null };
            } else if (submittedText !== '' && next.correct_answer && !isMultiBlankFill) {
              const normSub = normalizeMathSymbols(submittedText);
              const typedOk = !submissionIsDeterministicallyWrong(normSub, next.correct_answer, (qForMeasure || q)?.question_text)
                && (isTypedVerifyCorrect(normSub, next.correct_answer, qForMeasure || q)
                  || isShortValueInProseAnswer(normSub, next.correct_answer));
              next = { ...next, is_correct: typedOk ? true : false };
            }
          } else {
            // Measurement upgrade
            if (measureMode && !next.is_correct && submittedText && next.correct_answer && !hasDrawing) {
              const ok = isMeasurementVerifyCorrect(submittedText, next.correct_answer, qForMeasure || q, measureMode);
              if (ok) next = { ...next, is_correct: true, hint: 'Correct! ✓' };
            }

            // Drawing-only without AI Vision — rule-based "correct" is a false positive
            if (next.is_correct && !next.ai_evaluated && hasDrawing && submittedText === '') {
              next = { ...next, is_correct: false };
            }

            // Client-side prose/typed upgrade: backend returned false but student's short value
            // is embedded in a verbose AI prose correct_answer
            if (!next.is_correct && !isInteractiveDrawing(qForMeasure || q) && !isMultiBlankFill && !next.ai_evaluated && !hasDrawing && submittedText !== '' && next.correct_answer) {
              const normSub = normalizeMathSymbols(submittedText);
              const correctStr = String(next.correct_answer);
              if (!submissionIsDeterministicallyWrong(normSub, correctStr, (qForMeasure || q)?.question_text)
                && (isTypedVerifyCorrect(normSub, correctStr, qForMeasure || q) || isShortValueInProseAnswer(normSub, correctStr))) {
                next = { ...next, is_correct: true, is_partial: false };
              }
            }
          }
          // Clock-time questions are graded strictly rule-based: a pure time mark scheme (e.g.
          // "3:00 or 9:00") must match the colon format exactly, overriding the AI grader's
          // inconsistent verdict — so 3.00, 3.0, 3, "3 o'clock" are not accepted, only 3:00 / 9:00.
          // Multi-blank fills stay positional — a swapped pair of times must not match order-independently.
          if (!isInteractiveDrawing(qForMeasure || q) && !hasDrawing && submittedText !== '' && isPureClockTimeAnswer(next.correct_answer)) {
            const strict = isAnswerCorrect(normalizeMathSymbols(submittedText), String(next.correct_answer), { ordered: isMultiBlankFill });
            next = { ...next, is_correct: strict, is_partial: false };
          }
          // "What calculation did you do?" schemes are arithmetic expressions ("(30 × 10) ÷ 50").
          // AI Vision accepts a dropped operator ("(30 10) ÷ 50") — override deterministically.
          // Never upgrades; a matching structure leaves the verdict alone.
          if (!isInteractiveDrawing(qForMeasure || q) && !hasDrawing && (next.is_correct || next.is_partial)) {
            const arithmeticQ = qForMeasure || q;
            const arithmeticCorrectBlanks = Array.isArray(arithmeticQ?.answer_data?.correct_blanks)
              ? arithmeticQ.answer_data.correct_blanks
              : null;
            const arithmeticRawVal = q.type === 'grouped'
              ? ((answers[q.id] || {})[`${q.id}_sub_${groupedSubIdx}`])
              : answers[q.id];
            const arithmeticBlankValues = arithmeticRawVal && typeof arithmeticRawVal === 'object' && !Array.isArray(arithmeticRawVal) && Array.isArray(arithmeticRawVal.blanks)
              ? arithmeticRawVal.blanks
              : arithmeticRawVal;
            if (isMultiBlankFill && arithmeticCorrectBlanks && Array.isArray(arithmeticBlankValues)) {
              // Pooled blanks ("the rows may be in any order" / interchangeable boxes) are NOT bound to a
              // position, so blank i must be checked against EVERY blank's alternatives — comparing it to
              // correct_blanks[i] alone would fail a right answer written in a different order.
              const arithmeticOverlay = arithmeticQ?.answer_data?.overlay_layout;
              const arithmeticPooled = !!(arithmeticOverlay?.blanks_interchangeable
                || arithmeticOverlay?.rows_interchangeable
                || (Array.isArray(arithmeticOverlay?.interchangeable_row_indices) && arithmeticOverlay.interchangeable_row_indices.length));
              const altsOf = (cb) => (Array.isArray(cb) ? cb : [cb]).map((a) => String(a ?? '').trim()).filter(Boolean);
              const pooledAlts = arithmeticPooled ? arithmeticCorrectBlanks.flatMap(altsOf) : null;
              // A fill's `correct_answer` is a DISPLAY string, not a value list, so the whole-answer
              // compare below would read one blank's calculation against another's. Each blank carries
              // its own, and is wrong only when EVERY accepted alternative mismatches. One bad blank
              // costs the "correct" verdict, not the whole answer. Mirrors StudentPaperTake.jsx.
              const blankMismatched = arithmeticCorrectBlanks.some((cb, i) => {
                const alts = pooledAlts || altsOf(cb);
                const sub = String(arithmeticBlankValues[i] ?? '').trim();
                if (!alts.length || sub === '') return false;
                return alts.every((alt) => arithmeticCalculationMismatch(sub, alt) === true);
              });
              if (blankMismatched) {
                next = { ...next, is_correct: false, is_partial: true };
              }
            } else if (!isMultiBlankFill && submittedText !== ''
              && arithmeticCalculationMismatch(submittedText, next.correct_answer) === true) {
              next = { ...next, is_correct: false, is_partial: false };
            }
          }
          // Power mark schemes ("1³+2³+3³+4³") require the powers in the written form. Value
          // equality alone is not enough: 1³ = 1, so "1+2³+3³+4³" must not stay "Correct!".
          if (!isInteractiveDrawing(qForMeasure || q) && !hasDrawing && submittedText !== '' && next.is_correct) {
            const qq = qForMeasure || q;
            const correctBlanks = Array.isArray(qq?.answer_data?.correct_blanks)
              ? qq.answer_data.correct_blanks
              : null;
            const rawVal = q.type === 'grouped'
              ? ((answers[q.id] || {})[`${q.id}_sub_${groupedSubIdx}`])
              : answers[q.id];
            const blankValues = rawVal && typeof rawVal === 'object' && !Array.isArray(rawVal) && Array.isArray(rawVal.blanks)
              ? rawVal.blanks
              : rawVal;
            if (isMultiBlankFill && correctBlanks && Array.isArray(blankValues) && correctBlanks.length > 1) {
              let powerRequired = 0;
              let powerMatched = 0;
              correctBlanks.forEach((cb, i) => {
                const alts = (Array.isArray(cb) ? cb : [cb]).map((a) => String(a ?? '').trim()).filter(Boolean);
                if (!alts.length || !isNumericPowerExpression(alts[0])) return;
                powerRequired++;
                const sub = String(blankValues[i] ?? '').trim();
                if (alts.some((alt) => matchesNumericPower(sub, alt))) powerMatched++;
              });
              if (powerRequired > 0 && powerMatched < powerRequired) {
                next = { ...next, is_correct: false, is_partial: true };
              }
            } else if (!isMultiBlankFill
              && isNumericPowerExpression(next.correct_answer)
              && !matchesNumericPower(submittedText, String(next.correct_answer))) {
              next = { ...next, is_correct: false, is_partial: false };
            }
          }
          // Deterministic mismatches (single-letter "gm" for "G", re-typed unit junk, etc.) must never
          // stay "Correct!" just because AI Vision read the right digits inside a wrong answer.
          // Multi-blank fills are excluded: their submitted text is the boxes joined with "|" and their
          // correct_answer is a DISPLAY string of every box's value, so a whole-answer guard reads the
          // two lists as one value and one wrong box wipes out the server's partial credit.
          if (!isInteractiveDrawing(qForMeasure || q) && !hasDrawing && !isMultiBlankFill && submittedText !== '' && next.correct_answer
            && (next.is_correct || next.is_partial)
            && submissionIsDeterministicallyWrong(normalizeMathSymbols(submittedText), String(next.correct_answer), (qForMeasure || q)?.question_text)) {
            next = { ...next, is_correct: false, is_partial: false };
          }
          augmented[key] = next;
        }
        setVerifyResults((prev) => ({ ...prev, ...augmented }));
        const firstResult = Object.values(augmented)[0];
        if (firstResult) {
          if (firstResult.is_correct) {
            toast.success('Correct! ✓ — See the answer highlighted below.');
          } else if (firstResult.needs_review) {
            toast.warning('AI models disagreed — manual review recommended.');
          } else if (firstResult.is_partial) {
            toast.warning('Partially correct — see the full answer below.');
          } else if (firstResult.submitted_drawing && !firstResult.ai_evaluated) {
            // The server's feedback says WHY there is no verdict (handwriting unreadable → type it in
            // the box); the generic line hid that and left the student with nothing to act on.
            toast.info(firstResult.hint || 'Drawing submitted — compare with the correct answer below.');
          } else {
            toast.error('Incorrect — See the correct answer highlighted below.');
          }
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to verify answer.');
    } finally {
      setVerifyingAnswers(false);
    }
  };

  const currentQuestion = questions[currentIdx];

  // Preload verification state for the current question so the Verify button
  // can reflect any tester's rejection without the tester having to open it.
  useEffect(() => {
    if (!canVerifyMockQuestion || !currentQuestion) return;
    const bankId = String(currentQuestion.id).replace(/^bank_/, '');
    if (!bankId) return;
    if (getMockItemData("question", bankId)) return;
    loadMockVerifications("question", bankId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canVerifyMockQuestion, currentQuestion?.id]);

  const activeMeasurementMode = (() => {
    if (!currentQuestion) return null;
    if (currentQuestion.type === 'grouped') {
      const sub = (currentQuestion.sub_questions || [])[groupedSubIdx];
      if (sub) return measurementModeForSub(currentQuestion, sub);
    }
    return getMeasurementMode(currentQuestion);
  })();

  const isMathsPaper = /math/i.test(examData?.name || '');
  const effectiveMeasurementMode = isMathsPaper ? activeMeasurementMode : null;

  const activePxPerCm = (() => {
    if (!currentQuestion) return undefined;
    if (currentQuestion.type === 'grouped') {
      const sub = (currentQuestion.sub_questions || [])[groupedSubIdx];
      return getPxPerCm(sub, currentQuestion);
    }
    return getPxPerCm(currentQuestion);
  })();

  useEffect(() => {
    setShowProtractor(false);
    setShowRuler(false);
    setShowSetSquare(false);
    setShowCompass(false);
  }, [effectiveMeasurementMode]);

  const showProtractorBtn =
    effectiveMeasurementMode === MEASUREMENT_MODES.ANGLE ||
    effectiveMeasurementMode === MEASUREMENT_MODES.CONSTRUCT;
  const showRulerBtn =
    effectiveMeasurementMode === MEASUREMENT_MODES.LENGTH ||
    effectiveMeasurementMode === MEASUREMENT_MODES.CONSTRUCT;
  const showSetSquareBtn = effectiveMeasurementMode === MEASUREMENT_MODES.CONSTRUCT;
  const showCompassBtn = effectiveMeasurementMode === MEASUREMENT_MODES.CONSTRUCT;

  // ---- Annotation capture: route screenshot into the drawing pad as a floating image ----
  const handleAnnotationCapture = (base64Jpeg) => {
    if (!questions[currentIdx]) return;
    setAnnotationFloatImage(base64Jpeg);
    setAnnotationFloatSubId(null);
    setAnnotating(false);
  };

  const handleGroupedAnnotationCapture = (base64Jpeg) => {
    const q = questions[currentIdx];
    if (!q) return;
    const sub = (q.sub_questions || [])[groupedSubIdx];
    if (!sub) return;
    setAnnotationFloatImage(base64Jpeg);
    setAnnotationFloatSubId(sub.id || `sub_${groupedSubIdx}`);
    setAnnotating(false);
  };

  // ── Fill-in blank helpers (multi-fill / fill-in-the-blank / hub-spoke) ────────
  // Derive blank count from all possible sources so hub-spoke questions without
  // correct_blanks still get interactive inputs.
  const fillBlanks = (() => {
    if (!currentQuestion) return null;
    if (currentQuestion.type !== 'multi-fill' && currentQuestion.type !== 'fill-in-the-blank' && currentQuestion.type !== 'table') return null;
    const fromCorrect = currentQuestion.answer_data?.correct_blanks;
    if (Array.isArray(fromCorrect) && fromCorrect.length > 0) return fromCorrect;
    const fromLegacy = currentQuestion.blanks;
    if (Array.isArray(fromLegacy) && fromLegacy.length > 0) return fromLegacy;
    // Hub-spoke: blank_labels defines how many blanks exist even when correct_blanks is absent
    const fromLabels = currentQuestion.answer_data?.blank_labels;
    if (Array.isArray(fromLabels) && fromLabels.length > 0) return fromLabels.map(() => ['']);
    // Fallback: derive blank count from question text so students can always answer
    // even when correct_blanks was not yet stored (answer import pending).
    if (currentQuestion.type === 'fill-in-the-blank') return [['']];
    const textCount = countBlanksInText(currentQuestion.question_text);
    if (textCount > 0) return Array.from({ length: textCount }, () => ['']);
    // For table type: derive blank count from table_layout.grid editable cells
    const tableGrid = currentQuestion.answer_data?.table_layout?.grid;
    if (Array.isArray(tableGrid) && tableGrid.length > 0) {
      let maxB = -1;
      tableGrid.forEach((row) => (row || []).forEach((cell) => {
        if (cell && typeof cell.b === 'number' && cell.b > maxB) maxB = cell.b;
      }));
      if (maxB >= 0) return Array.from({ length: maxB + 1 }, () => ['']);
    }
    return null;
  })();
  const fillN = fillBlanks ? fillBlanks.length : 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography>Loading exam...</Typography>
        </Box>
      </Box>
    );
  }

  if (!currentQuestion) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Failed to load exam questions.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/student/mock-exams')}>
          Back to Exams
        </Button>
      </Box>
    );
  }

  const answerLaterCount = Object.values(statuses).filter((s) => s === STATUS.ANSWER_LATER).length;
  const flaggedCount = Object.values(statuses).filter((s) => s === STATUS.FLAGGED).length;
  const answeredCount = questions.filter((q) => computeQuestionStatus(q, answers[q.id]) === STATUS.ANSWERED).length;
  const partialCount = questions.filter((q) => computeQuestionStatus(q, answers[q.id]) === STATUS.PARTIAL).length;
  const unansweredCount = questions.length - answeredCount - partialCount - answerLaterCount - flaggedCount;
  const isFlagged = flagged.has(currentIdx);
  const currentAnswer = answers[currentQuestion?.id || currentIdx];

  // Verify result lookup for the current question
  const currentVerifyKey = currentQuestion?.type === 'grouped'
    ? `${currentQuestion.id}_sub_${groupedSubIdx}`
    : String(currentQuestion?.id ?? '');
  const currentVerifyResult = verifyResults[currentVerifyKey] ?? null;

  // Section banner: show when the student reaches the first question of a new section
  const prevSectionTitle = currentIdx > 0 ? displaySectionTitle(questions[currentIdx - 1]?.section_title) : null;
  const currentSectionTitle = displaySectionTitle(currentQuestion.section_title);
  const suppressCurrentSection = !!currentQuestion.section_title && !currentSectionTitle;
  const isNewSection = !!currentSectionTitle && currentSectionTitle !== prevSectionTitle;

  // Booklet-accurate question counter — compute exam-wide position index
  // so both header "Question X of Y" and palette use the same numbering.
  const examPositionByIndex = (() => {
    const positions = [];
    let running = 1;
    questions.forEach((q, i) => {
      if (q.type === 'grouped' && q.sub_questions?.length) {
        if (isComprehensionPassage(q)) {
          const len = q.sub_questions.length;
          positions[i] = { start: running, end: running + len - 1 };
          running += len;
        } else {
          positions[i] = { start: running, end: running };
          running += 1;
        }
      } else {
        positions[i] = { start: running, end: running };
        running += 1;
      }
    });
    return { positions, total: running - 1 };
  })();

  const questionDisplayLabel = (() => {
    const pn = currentQuestion?.paper_number;
    if (pn != null && String(pn).trim() !== '') return String(pn).trim();
    const pos = examPositionByIndex.positions[currentIdx];
    return pos ? (pos.start === pos.end ? String(pos.start) : `${pos.start}–${pos.end}`) : String(currentIdx + 1);
  })();

  const groupedPaperRange = (() => {
    if (currentQuestion?.type !== 'grouped') return null;
    // Only show a range for comprehension passages (English); math grouped = single question number
    if (!isComprehensionPassage(currentQuestion)) return null;
    const nums = (currentQuestion.sub_questions ?? []).map((s) => s.paper_number).filter(Boolean);
    if (nums.length === 0) return null;
    return nums.length === 1 ? nums[0] : `${nums[0]}–${nums[nums.length - 1]}`;
  })();

  const isCurrentComprehensionPassage = isComprehensionPassage(currentQuestion);
  const rawPassage = isCurrentComprehensionPassage
    ? (currentQuestion?.question_text || currentQuestion?.group_title || '')
    : '';
  const passageStemText = isCurrentComprehensionPassage
    ? cleanStemText(rawPassage, { preserveLineMarkers: true })
    : '';
  const passageHasHtml = passageStemText ? /<[a-zA-Z]/.test(passageStemText) : false;
  const passageHasBold = passageStemText ? passageStemText.includes('**') : false;
  const passageHasTable = passageStemText ? (passageStemText.includes('|') || passageStemText.includes('<table')) : false;
  const passageHasLatex = passageStemText ? (passageStemText.includes('\\(') || passageStemText.includes('$$')) : false;
  const passageStemImages = isCurrentComprehensionPassage ? (() => {
    const htmlEmbedded = new Set();
    const html = String(currentQuestion?.question_text || '');
    const re = /<img[^>]*\ssrc=["']([^"']+)["']/gi;
    let m;
    while ((m = re.exec(html)) !== null) htmlEmbedded.add(m[1]);
    const seen = new Set();
    const result = [];
    const add = (url) => { if (url && !seen.has(url) && !htmlEmbedded.has(url)) { seen.add(url); result.push(url); } };
    if (Array.isArray(currentQuestion?.image_urls)) currentQuestion.image_urls.filter(Boolean).forEach(add);
    add(currentQuestion?.image_url);
    return result;
  })() : [];

  // Total booklet questions: use examPositionByIndex total, fallback to paper_number scan
  const totalBookletQuestions = examPositionByIndex.total || (() => {
    for (let i = questions.length - 1; i >= 0; i--) {
      const q = questions[i];
      if (q?.type === 'grouped' && q?.sub_questions?.length && isComprehensionPassage(q)) {
        const subs = q.sub_questions;
        for (let j = subs.length - 1; j >= 0; j--) {
          const spn = subs[j]?.paper_number;
          if (spn) { const n = parseInt(spn, 10); if (!isNaN(n)) return n; }
        }
      }
      const pn = q?.paper_number;
      if (pn) { const n = parseInt(pn, 10); if (!isNaN(n)) return n; }
    }
    return questions.length;
  })();

  const { totalMarks, marksAttempted } = (() => {
    let total = 0;
    let attempted = 0;
    questions.forEach((q, i) => {
      const m = q.default_marks ?? q.marks ?? q.answer_data?.marks ?? 1;
      total += m;
      if (!isAnswerEmpty(answers[q.id]) || statuses[i] === STATUS.FLAGGED) attempted += m;
    });
    return { totalMarks: total, marksAttempted: attempted };
  })();

  return (
    <AnnotateProvider annotating={annotating} onToggle={() => setAnnotating((a) => !a)}>
    <MeasurementToolsProvider
      measurementMode={effectiveMeasurementMode}
      showProtractor={showProtractor}
      showRuler={showRuler}
      onToggleProtractor={() => setShowProtractor((v) => !v)}
      onToggleRuler={() => setShowRuler((v) => !v)}
    >
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* ---- Top Header Bar ---- */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 1.5, sm: 3 },
          py: 1.5,
          bgcolor: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            {examData?.name || 'Mock Exam'}
          </Typography>
          {isTestMode && (
            <Alert severity="info" sx={{ py: 0, px: 1.5, fontSize: '0.75rem', fontWeight: 700, '& .MuiAlert-icon': { fontSize: 16, mr: 0.5 } }}>
              PRACTICE MODE
            </Alert>
          )}
        </Box>

        {/* Timer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.75,
            borderRadius: 2,
            bgcolor: timeLeft < 300 ? '#fff3e0' : '#f5f5f5',
            border: '1px solid',
            borderColor: timeLeft < 300 ? 'warning.main' : '#e0e0e0',
          }}
        >
          <AccessTime sx={{ fontSize: 20, color: timeLeft < 300 ? 'warning.main' : 'text.secondary' }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontFamily: 'monospace',
              color: timeLeft < 300 ? 'warning.main' : 'text.primary',
            }}
          >
            {formatTime(timeLeft)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => setPaused((p) => !p)}
            disabled={submitting}
            startIcon={paused ? <PlayCircle /> : <PauseCircle />}
            sx={{
              fontWeight: 700,
              textTransform: 'none',
              color: paused ? 'success.main' : 'text.secondary',
              borderColor: paused ? 'success.main' : '#e0e0e0',
              '&:hover': { bgcolor: paused ? '#f0fdf4' : 'action.hover' },
            }}
          >
            {paused ? 'Resume' : 'Pause'}
          </Button>
          <Button
            variant="contained"
            onClick={() => setConfirmSubmit(true)}
            disabled={submitting}
            sx={{
              bgcolor: '#ef5350',
              color: 'white',
              fontWeight: 700,
              px: 3,
              textTransform: 'none',
              '&:hover': { bgcolor: '#c62828' },
            }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : isTestMode ? 'Finish Practice' : 'Submit Exam'}
          </Button>
        </Box>
      </Box>

      {/* ---- Practice Mode Banner ---- */}
      {isTestMode && !paused && (
        <Alert
          severity="info"
          sx={{ borderRadius: 0, py: 0.5, px: 3, '& .MuiAlert-message': { display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' } }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
            PRACTICE MODE — Answers are not graded. Use "Check Answer" on each question to see the correct answer.
          </Typography>
        </Alert>
      )}

      {/* ---- Pause Overlay — covers questions so student can't read ahead ---- */}
      {paused && (
        <Box
          sx={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            bgcolor: 'rgba(15,15,15,0.92)',
            zIndex: 300,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
          }}
        >
          <PauseCircle sx={{ fontSize: 72, color: 'white', opacity: 0.6 }} />
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
            Exam Paused
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
            Timer is stopped. Click Resume when you are ready to continue.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayCircle />}
            onClick={() => setPaused(false)}
            sx={{
              bgcolor: '#4caf50',
              color: 'white',
              fontWeight: 700,
              px: 5,
              py: 1.5,
              fontSize: '1rem',
              textTransform: 'none',
              '&:hover': { bgcolor: '#388e3c' },
            }}
          >
            Resume Exam
          </Button>
        </Box>
      )}

      {/* ---- Main Content ---- */}
      <Box sx={{ display: 'flex', flex: 1, gap: 0, overflow: 'hidden', flexDirection: { xs: 'column', lg: 'row' }, minWidth: 0 }}>
        {/* Passage panel (left) — shown when OPEN PASSAGE for comprehension questions */}
        {passageOpen && isComprehensionPassage(currentQuestion) && (
          <Box
            sx={{
              width: { xs: '100%', lg: supplementaryOpen ? '35%' : '55%' },
              minWidth: { xs: 0, lg: 280 },
              maxWidth: { xs: '100%', lg: supplementaryOpen ? 460 : 600 },
              p: 2.5,
              overflow: 'auto',
              bgcolor: '#fafafa',
              borderRight: { xs: 'none', lg: '2px solid #e0e0e0' },
              borderBottom: { xs: '2px solid #e0e0e0', lg: 'none' },
              boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
              position: { xs: 'relative', lg: 'sticky' },
              top: 0,
              height: { xs: 'auto', lg: 'calc(100vh - 64px)' },
              maxHeight: { xs: '40vh', lg: 'none' },
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                Reading Passage
              </Typography>
              <Tooltip title="Minimize passage">
                <Button
                  size="small"
                  onClick={() => setPassageOpen(false)}
                  sx={{ minWidth: 'auto', p: 0.5, color: 'text.secondary' }}
                >
                  <ExpandMore />
                </Button>
              </Tooltip>
            </Box>
            {passageHasTable || passageHasLatex || passageHasHtml || passageHasBold ? (
              <HtmlWithLatex html={passageStemText} style={{ fontSize: '0.95rem', lineHeight: 1.75 }} />
            ) : (
              <PassageWithLineNumbers
                text={passageStemText}
                wrapWidth={computePassageWrapWidth(rawPassage, currentQuestion?.sub_questions || [])}
              />
            )}
            {passageStemImages.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {passageStemImages.map((src, i) => (
                  <Box key={i} component="img" src={resolveQuestionHtmlAssetUrl(src) || src} alt="" onError={(e) => { e.target.style.display = 'none'; }} sx={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 1 }} />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Supplementary Booklet panel — shown when VIEW SOURCES button is active */}
        {supplementaryOpen && hasSupplementaryBooklet(currentQuestion) && (
          <Box
            sx={{
              width: { xs: '100%', lg: passageOpen ? '35%' : '55%' },
              minWidth: { xs: 0, lg: 280 },
              maxWidth: { xs: '100%', lg: passageOpen ? 460 : 600 },
              overflow: 'hidden',
              bgcolor: '#faf5ff',
              borderRight: { xs: 'none', lg: '2px solid #c4b5fd' },
              borderBottom: { xs: '2px solid #c4b5fd', lg: 'none' },
              boxShadow: '2px 0 8px rgba(92,53,176,0.10)',
              position: { xs: 'relative', lg: 'sticky' },
              top: 0,
              height: { xs: 'auto', lg: 'calc(100vh - 64px)' },
              maxHeight: { xs: '40vh', lg: 'none' },
              minHeight: { xs: 200, lg: 0 },
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid #ddd6fe' }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#5b21b6' }}
              >
                Supplementary Sources
              </Typography>
              <Tooltip title="Close supplementary sources">
                <Button
                  size="small"
                  onClick={() => setSupplementaryOpen(false)}
                  sx={{ minWidth: 'auto', p: 0.5, color: '#7c3aed' }}
                >
                  <ExpandMore />
                </Button>
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <iframe
                src={currentQuestion.supplementary_booklet_url}
                width="100%"
                height="100%"
                style={{ border: 'none', display: 'block' }}
                title="Supplementary Sources Booklet"
              />
            </Box>
          </Box>
        )}

        {/* Right: Question Area */}
        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 1.5, sm: 3 }, pb: 3, pt: 0, overflow: 'auto' }}>
          {/* Question header bar — sticky so passage/flag/annotate buttons stay visible when scrolling */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: '#f5f5f5',
              mx: { xs: -1.5, sm: -3 },
              px: { xs: 1.5, sm: 3 },
              pt: 2,
              pb: 1.5,
              mb: 1,
              borderBottom: '1px solid #e8e8e8',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {groupedPaperRange
                    ? `Questions ${groupedPaperRange}`
                    : `Question ${questionDisplayLabel} of ${totalBookletQuestions}`}
                  {(() => {
                    let m = currentQuestion?.default_marks ?? currentQuestion?.marks ?? currentQuestion?.answer_data?.marks;
                    if (currentQuestion?.type !== 'grouped' && isFillQuestionType(currentQuestion)) {
                      const fillTotal = getDisplayFillMarksTotal(currentQuestion);
                      if (fillTotal > 0) m = fillTotal;
                    }
                    if (m == null && currentQuestion?.type === 'grouped' && currentQuestion?.sub_questions?.length) {
                      const parentAd = currentQuestion.answer_data || {};
                      if (parentAd.marks_mode === 'fixed') {
                        m = parentAd.marks_total ?? currentQuestion.default_marks ?? m;
                      } else {
                        const total = currentQuestion.sub_questions.reduce(
                          (sum, s) => sum + (s.default_marks ?? s.marks ?? s.answer_data?.marks ?? 0), 0
                        );
                        if (total > 0) m = total;
                      }
                    }
                    return m != null ? ` · ${m} mark${m !== 1 ? 's' : ''}` : '';
                  })()}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isComprehensionPassage(currentQuestion) && (
                <Tooltip title={passageOpen ? 'Close passage (P)' : 'Open passage (P)'}>
                  <Button
                    size="small"
                    variant={passageOpen ? 'contained' : 'outlined'}
                    onClick={() => setPassageOpen((o) => !o)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: passageOpen ? '#1a1a1a' : 'transparent',
                      color: passageOpen ? 'white' : 'text.primary',
                      borderColor: passageOpen ? '#1a1a1a' : '#bbb',
                      '&:hover': { bgcolor: passageOpen ? '#333' : 'action.hover' },
                    }}
                  >
                    {passageOpen ? 'CLOSE PASSAGE' : 'OPEN PASSAGE'}
                  </Button>
                </Tooltip>
              )}
              {hasSupplementaryBooklet(currentQuestion) && (
                <Tooltip title={supplementaryOpen ? 'Close supplementary sources' : 'Open supplementary booklet / sources'}>
                  <Button
                    size="small"
                    variant={supplementaryOpen ? 'contained' : 'outlined'}
                    onClick={() => setSupplementaryOpen((o) => !o)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: supplementaryOpen ? '#5c35b0' : 'transparent',
                      color: supplementaryOpen ? 'white' : '#5c35b0',
                      borderColor: supplementaryOpen ? '#5c35b0' : '#c4b5fd',
                      '&:hover': { bgcolor: supplementaryOpen ? '#4c1d95' : 'rgba(92,53,176,0.06)' },
                    }}
                  >
                    {supplementaryOpen ? 'CLOSE SOURCES' : 'VIEW SOURCES'}
                  </Button>
                </Tooltip>
              )}
              <Tooltip title={isFlagged ? 'Unflag this question' : 'Flag for review'}>
                <Button
                  size="small"
                  variant={isFlagged ? 'contained' : 'outlined'}
                  startIcon={flaggingInProgress[currentIdx] ? <CircularProgress size={16} color="inherit" /> : (isFlagged ? <Flag fontSize="small" /> : <FlagOutlined fontSize="small" />)}
                  onClick={() => toggleFlag()}
                  disabled={flaggingInProgress[currentIdx]}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: isFlagged ? 'white' : 'warning.main',
                    borderColor: 'warning.main',
                    bgcolor: isFlagged ? 'warning.main' : 'transparent',
                    '&:hover': { bgcolor: isFlagged ? 'warning.dark' : 'warning.50' },
                  }}
                >
                  {isFlagged ? 'Flagged' : 'Flag'}
                </Button>
              </Tooltip>
              {canVerifyMockQuestion && currentQuestion && (() => {
                const bankId = String(currentQuestion.id).replace(/^bank_/, '');
                const mockItemData = getMockItemData("question", bankId);
                const myVerdicts = mockItemData?.my_verdicts;
                const aggregate = mockItemData?.aggregate;
                const btnColors = getVerifierButtonColors(myVerdicts, aggregate);
                const isRejected = (myVerdicts?.web === "rejected" || myVerdicts?.mobile === "rejected" || (aggregate?.rejected ?? 0) > 0);
                return (
                <Tooltip title={isRejected ? "This question was rejected — review and re-approve (Tester mode)" : "Verify this question (Tester mode)"}>
                  <Button
                    size="small"
                    variant={btnColors.variant}
                    startIcon={<VerifiedUserIcon fontSize="small" />}
                    onClick={() => {
                      loadMockVerifications("question", bankId);
                      setMockVerifyQuestionFull(null);
                      setMockVerifyQuestionLoading(true);
                      api.get(`/admin/question-bank/${bankId}`)
                        .then((r) => setMockVerifyQuestionFull(r.data?.data ?? null))
                        .catch(() => {})
                        .finally(() => setMockVerifyQuestionLoading(false));
                      setMockVerifyDialogOpen(true);
                    }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: btnColors.color,
                      borderColor: "info.main",
                      bgcolor: btnColors.bgcolor,
                      "&:hover": { bgcolor: btnColors.variant === "contained" ? btnColors.bgcolor : "info.50" },
                    }}
                  >
                    {formatVerifierButtonLabel(myVerdicts, aggregate)}
                  </Button>
                </Tooltip>
                );
              })()}
              {showProtractorBtn && (
                <Tooltip title={showProtractor ? 'Hide protractor' : 'Show protractor on diagram'}>
                  <Button
                    size="small"
                    variant={showProtractor ? 'contained' : 'outlined'}
                    onClick={() => setShowProtractor((v) => !v)}
                    sx={{
                      minWidth: 0,
                      px: 1,
                      color: showProtractor ? 'white' : '#1565c0',
                      borderColor: '#1565c0',
                      bgcolor: showProtractor ? '#1565c0' : 'transparent',
                      '&:hover': { bgcolor: showProtractor ? '#0d47a1' : 'rgba(21,101,192,0.06)' },
                    }}
                  >
                    <ProtractorIcon fontSize="small" />
                  </Button>
                </Tooltip>
              )}
              {showRulerBtn && (
                <Tooltip title={showRuler ? 'Hide ruler' : 'Show ruler on diagram'}>
                  <Button
                    size="small"
                    variant={showRuler ? 'contained' : 'outlined'}
                    onClick={() => setShowRuler((v) => !v)}
                    sx={{
                      minWidth: 0,
                      px: 1,
                      color: showRuler ? 'white' : 'success.main',
                      borderColor: 'success.main',
                      bgcolor: showRuler ? 'success.main' : 'transparent',
                      '&:hover': { bgcolor: showRuler ? 'success.dark' : 'rgba(46,125,50,0.06)' },
                    }}
                  >
                    <StraightenIcon fontSize="small" />
                  </Button>
                </Tooltip>
              )}
              {showSetSquareBtn && (
                <Tooltip title={showSetSquare ? 'Hide set square' : 'Show set square'}>
                  <Button size="small" variant={showSetSquare ? 'contained' : 'outlined'} onClick={() => setShowSetSquare((v) => !v)} sx={{ minWidth: 0, px: 1, color: showSetSquare ? 'white' : '#1565c0', borderColor: '#1565c0', bgcolor: showSetSquare ? '#1565c0' : 'transparent', '&:hover': { bgcolor: showSetSquare ? '#0d47a1' : 'rgba(21,101,192,0.06)' } }}>
                    <SetSquareIcon fontSize="small" />
                  </Button>
                </Tooltip>
              )}
              {showCompassBtn && (
                <Tooltip title={showCompass ? 'Hide compass' : 'Show compass'}>
                  <Button size="small" variant={showCompass ? 'contained' : 'outlined'} onClick={() => setShowCompass((v) => !v)} sx={{ minWidth: 0, px: 1, color: showCompass ? 'white' : '#6a1b9a', borderColor: '#6a1b9a', bgcolor: showCompass ? '#6a1b9a' : 'transparent', '&:hover': { bgcolor: showCompass ? '#4a148c' : 'rgba(106,27,154,0.06)' } }}>
                    <AdjustIcon fontSize="small" />
                  </Button>
                </Tooltip>
              )}
              <Tooltip title={annotating ? 'Hide annotation tools' : 'Annotate / Circle on question'}>
                <Button
                  size="small"
                  variant={annotating ? 'contained' : 'outlined'}
                  onClick={() => setAnnotating((a) => !a)}
                  sx={{
                    minWidth: 0,
                    px: 1,
                    color: annotating ? 'white' : 'text.secondary',
                    borderColor: annotating ? 'warning.main' : '#e0e0e0',
                    bgcolor: annotating ? 'warning.main' : 'transparent',
                    '&:hover': { bgcolor: annotating ? 'warning.dark' : 'action.hover' },
                  }}
                >
                  <EditIcon fontSize="small" />
                </Button>
              </Tooltip>
            </Box>
          </Box>

          {/* Section title banner — always shown for any question in a named section */}
          {currentSectionTitle && (
            <Box
              sx={{
                mx: { xs: -1.5, sm: -3 },
                px: { xs: 1.5, sm: 3 },
                py: 1.25,
                mb: currentQuestion.section_instructions ? 0 : 1.5,
                bgcolor: isNewSection ? '#1565c0' : '#e3f2fd',
                color: isNewSection ? 'white' : '#0d3c70',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: isNewSection ? 'none' : '1px solid #bbdefb',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontSize: '0.85rem',
                }}
              >
                {currentSectionTitle}
              </Typography>
            </Box>
          )}
          {/* Section instructions — always shown for any question that has them */}
          {currentQuestion.section_instructions && !suppressCurrentSection && (
            <Box
              sx={{
                mx: { xs: -1.5, sm: -3 },
                px: { xs: 1.5, sm: 3 },
                py: 1.25,
                mb: 1.5,
                bgcolor: '#e3f2fd',
                borderBottom: '2px solid #1565c0',
                '& ol': { pl: 2, m: 0 },
                '& li': { mb: 0.75, fontSize: '0.875rem', color: '#0d3c70', lineHeight: 1.6 },
              }}
            >
              <FormatInstructions text={currentQuestion.section_instructions} typographySx={{ color: '#0d3c70' }} />
            </Box>
          )}

          {/* Question Card — wrapped in annotation canvas overlay */}
          <QuestionAnnotationCanvas
            questionIdx={currentIdx}
            visible={annotating}
            onCapture={
              currentQuestion?.type === 'grouped'
                ? handleGroupedAnnotationCapture
                : ['short-answer', 'essay'].includes(currentQuestion?.type)
                ? handleAnnotationCapture
                : undefined
            }
          >
            <Paper
              sx={{
                p: 3,
                borderRadius: annotating ? '0 0 12px 12px' : 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                mb: 2,
                maxWidth: '100%',
                minWidth: 0,
              }}
            >
              {currentQuestion.type === 'grouped' ? (
                <>
                  <GroupedQuestion
                    question={currentQuestion}
                    answerMap={currentAnswer}
                    onSubAnswerChange={handleGroupedSubAnswer}
                    questionIdx={currentIdx}
                    scratchDrawingsRef={scratchDrawingsRef}
                    passageOpen={passageOpen}
                    onSubIdxChange={setGroupedSubIdx}
                    pendingFloatImage={annotationFloatImage}
                    onFloatImageConsumed={() => { setAnnotationFloatImage(null); setAnnotationFloatSubId(null); }}
                    pendingFloatSubId={annotationFloatSubId}
                    showMathTools={isMathsPaper}
                    showAnswerUnit={showAnswerUnit}
                    isTestMode={isTestMode}
                    onCheckSubAnswer={handleVerifySingleQuestion}
                    verifyResults={verifyResults}
                    verifyingAnswers={verifyingAnswers}
                  />
                  {isTestMode && (
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        color="info"
                        onClick={handleVerifySingleQuestion}
                        disabled={verifyingAnswers}
                        sx={{ fontWeight: 700, textTransform: 'none', fontSize: '0.8rem' }}
                      >
                        {verifyingAnswers ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} /> : null}
                        {verifyingAnswers ? 'Checking…' : '✓ Check Answer (current part)'}
                      </Button>
                      {(() => { const _dp = getVerifyDisplayProps(currentVerifyResult); return currentVerifyResult && _dp ? (
                        <Alert
                          severity={_dp.severity}
                          sx={{ flex: 1, py: 0.5, fontSize: '0.85rem', borderRadius: 2 }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {currentVerifyResult.correct_answer ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {_dp.prefix && (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <span style={{ fontWeight: 700 }}>{_dp.prefix}</span>
                                  </Box>
                                )}
                                {isBoxModeImageFillQuestion(currentQuestion)
                                  ? renderImageFillCorrectAnswer(currentQuestion, _dp.answerLabel, currentVerifyResult.correct_answer)
                                  : renderCorrectAnswer(currentVerifyResult.correct_answer, _dp.answerLabel, currentQuestion?.type === 'essay')}
                              </Box>
                            ) : (
                              <span style={{ fontWeight: 700 }}>{_dp.prefix ?? ''}</span>
                            )}
                            {currentVerifyResult.submitted_answer && (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, opacity: 0.85 }}>
                                <span>Your answer:</span>
                                <span style={{ fontStyle: 'italic' }}>{currentVerifyResult.submitted_answer}</span>
                              </Box>
                            )}
                            {currentVerifyResult.submitted_drawing && (
                              <Box>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
                                  Your drawing:
                                </Typography>
                                <Box
                                  component="img"
                                  src={currentVerifyResult.submitted_drawing}
                                  alt="Your handwritten answer"
                                  sx={{
                                    maxWidth: '100%',
                                    maxHeight: 180,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: currentVerifyResult.is_correct ? 'success.light' : 'error.light',
                                    display: 'block',
                                    bgcolor: 'white',
                                  }}
                                />
                                {_dp.aiEvaluated && !_dp.needsReview && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                                    ✦ Evaluated by AI Vision
                                  </Typography>
                                )}
                                {_dp.needsReview && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.dark', fontStyle: 'italic' }}>
                                    ⚠ AI models gave conflicting verdicts — ask your tutor to review
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Alert>
                      ) : null; })()}
                    </Box>
                  )}
                  {isTestMode ? (() => {
                    const summary = summarizePartVerdicts(currentQuestion, verifyResults);
                    if (!summary) return null;
                    return (
                      <Alert severity={summary.severity} sx={{ mt: 1.5, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          This question: {summary.text}
                        </Typography>
                        {summary.note ? (
                          <Typography variant="caption" color="text.secondary">{summary.note}</Typography>
                        ) : null}
                      </Alert>
                    );
                  })() : null}
                </>
              ) : (
                <>
                  <Box ref={questionDisplayRef}>
                  {fillBlanks ? (
                    <QuestionPaperLayout
                      hideBlankMarks
                      question={currentQuestion}
                      questionNumber={currentIdx + 1}
                      showAnswers={false}
                      showMathTools={isMathsPaper}
                      inputValues={(() => {
                        const arr = Array.isArray(currentAnswer) ? [...currentAnswer] : (fillN === 1 ? [currentAnswer ?? ''] : []);
                        return Array.from({ length: fillN }, (_, i) => arr[i] ?? '');
                      })()}
                      onInputChange={(idx, value) => {
                        const arr = Array.isArray(currentAnswer) ? [...currentAnswer] : (fillN === 1 ? [currentAnswer ?? ''] : []);
                        const next = Array.from({ length: fillN }, (_, i) => (i === idx ? value : arr[i] ?? ''));
                        handleAnswer(fillN === 1 ? next[0] : next);
                      }}
                    />
                  ) : (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <HtmlWithLatex html={currentQuestion?.question_text || `Question ${currentIdx + 1}`} />
                      </Box>
                      {(() => {
                        // An interactive drawing hides only the ONE image its canvas paints. Any other
                        // figure the question carries (the dice a probability line refers to, a data
                        // table) is context the student still has to read.
                        const canvasKey = isInteractiveDrawing(currentQuestion)
                          ? interactiveImgKey(interactiveImageOf(currentQuestion))
                          : null;
                        let imgs = collectQuestionImages(currentQuestion)
                          .filter((u) => !canvasKey || interactiveImgKey(u) !== canvasKey);
                        // Box-mode image-fill renders its overlay image interactively below
                        // (ImageOverlayFillInput); keep only the other context images here so
                        // the overlay diagram is not duplicated as a static image.
                        if (isBoxModeImageFillQuestion(currentQuestion)) {
                          const overlayBase = String(currentQuestion.answer_data?.overlay_layout?.image_url || '').split('/').pop().split('?')[0];
                          imgs = imgs.filter((u) => String(u || '').split('/').pop().split('?')[0] !== overlayBase);
                        }
                        return imgs.length > 0 ? (
                          <MeasurementWorkspace
                            images={imgs}
                            measurementMode={isMathsPaper && !canvasKey ? getMeasurementMode(currentQuestion) : null}
                            caption={!canvasKey && isPlotPointsQuestion(currentQuestion) ? 'Plot on this diagram' : undefined}
                            pxPerCm={getPxPerCm(currentQuestion)}
                          />
                        ) : null;
                      })()}
                      {currentQuestion?.answer_data?.geometry_config ? (
                        <GeometryMarkSegment
                          config={currentQuestion.answer_data.geometry_config}
                          value={currentAnswer}
                          onChange={handleAnswer}
                        />
                      ) : null}
                    </>
                  )}
                  </Box>
                  {!fillBlanks && (
                    <Box sx={{ mt: 2 }}>
                      <QuestionInput
                        key={currentQuestion?.id || currentIdx}
                        question={currentQuestion}
                        answer={currentAnswer}
                        onChange={handleAnswer}
                        questionIdx={currentIdx}
                        showMathTools={isMathsPaper}
                        showAnswerUnit={showAnswerUnit}
                        isTestMode={isTestMode}
                        onCheckAnswer={handleVerifySingleQuestion}
                        verifyingAnswers={verifyingAnswers}
                        verifyResult={currentVerifyResult}
                        contextImageUrl={questionContextSnapshot}
                        questionImages={collectAllQuestionImageEntries(currentQuestion)}
                        pendingFloatImage={annotationFloatImage}
                        onFloatImageConsumed={() => { setAnnotationFloatImage(null); setAnnotationFloatSubId(null); }}
                      />
                      {/* Check Answer button for non-TypeDraw question types */}
                      {isTestMode && !['short-answer', 'essay'].includes(currentQuestion.type) && (
                        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            color="info"
                            onClick={handleVerifySingleQuestion}
                            disabled={verifyingAnswers}
                            sx={{ fontWeight: 700, textTransform: 'none', fontSize: '0.8rem' }}
                          >
                            {verifyingAnswers ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} /> : null}
                            {verifyingAnswers ? 'Checking…' : '✓ Check Answer'}
                          </Button>
                          {(() => { const _dp = getVerifyDisplayProps(currentVerifyResult); return currentVerifyResult && _dp ? (
                            <Alert
                              severity={_dp.severity}
                              sx={{ flex: 1, py: 0.5, fontSize: '0.85rem', borderRadius: 2 }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {currentVerifyResult.correct_answer ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {_dp.prefix && (
                                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        <span style={{ fontWeight: 700 }}>{_dp.prefix}</span>
                                      </Box>
                                    )}
                                    {isBoxModeImageFillQuestion(currentQuestion)
                                  ? renderImageFillCorrectAnswer(currentQuestion, _dp.answerLabel, currentVerifyResult.correct_answer)
                                  : renderCorrectAnswer(currentVerifyResult.correct_answer, _dp.answerLabel, currentQuestion?.type === 'essay')}
                                  </Box>
                                ) : (
                                  <span style={{ fontWeight: 700 }}>{_dp.prefix ?? ''}</span>
                                )}
                                {currentVerifyResult.submitted_answer && (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, opacity: 0.85 }}>
                                    <span>Your answer:</span>
                                    <span style={{ fontStyle: 'italic' }}>{currentVerifyResult.submitted_answer}</span>
                                  </Box>
                                )}
                                {currentVerifyResult.submitted_drawing && (
                                  <Box>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
                                      Your drawing:
                                    </Typography>
                                    <Box
                                      component="img"
                                      src={currentVerifyResult.submitted_drawing}
                                      alt="Your handwritten answer"
                                      sx={{
                                        maxWidth: '100%',
                                        maxHeight: 180,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: currentVerifyResult.is_correct ? 'success.light' : 'error.light',
                                        display: 'block',
                                        bgcolor: 'white',
                                      }}
                                    />
                                    {_dp.aiEvaluated && !_dp.needsReview && (
                                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                                        ✦ Evaluated by AI Vision
                                      </Typography>
                                    )}
                                    {_dp.needsReview && (
                                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.dark', fontStyle: 'italic' }}>
                                        ⚠ AI models gave conflicting verdicts — ask your tutor to review
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Alert>
                          ) : null; })()}
                        </Box>
                      )}
                      {/* Scratch pad only for question types that may need working (not for pure MCQ/true-false) */}
                      {['multiple-select', 'fill-in-the-blank', 'multi-fill', 'table', 'image-fill', 'matching', 'ordering'].includes(currentQuestion.type) && (
                        <ScratchPad
                          questionIdx={currentIdx}
                          scratchDrawingsRef={scratchDrawingsRef}
                          marks={currentQuestion?.default_marks ?? currentQuestion?.marks ?? currentQuestion?.answer_data?.marks}
                        />
                      )}
                    </Box>
                  )}
                  {/* Check Answer for fillBlanks path */}
                  {fillBlanks && isTestMode && (
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        color="info"
                        onClick={handleVerifySingleQuestion}
                        disabled={verifyingAnswers}
                        sx={{ fontWeight: 700, textTransform: 'none', fontSize: '0.8rem' }}
                      >
                        {verifyingAnswers ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} /> : null}
                        {verifyingAnswers ? 'Checking…' : '✓ Check Answer'}
                      </Button>
                      {(() => { const _dp = getVerifyDisplayProps(currentVerifyResult); return currentVerifyResult && _dp ? (
                        <Alert
                          severity={_dp.severity}
                          sx={{ flex: 1, py: 0.5, fontSize: '0.85rem', borderRadius: 2 }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {currentVerifyResult.correct_answer ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {_dp.prefix && (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <span style={{ fontWeight: 700 }}>{_dp.prefix}</span>
                                  </Box>
                                )}
                                {isBoxModeImageFillQuestion(currentQuestion)
                                  ? renderImageFillCorrectAnswer(currentQuestion, _dp.answerLabel, currentVerifyResult.correct_answer)
                                  : renderCorrectAnswer(currentVerifyResult.correct_answer, _dp.answerLabel, currentQuestion?.type === 'essay')}
                              </Box>
                            ) : (
                              <span style={{ fontWeight: 700 }}>{_dp.prefix ?? ''}</span>
                            )}
                            {currentVerifyResult.submitted_answer && (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, opacity: 0.85 }}>
                                <span>Your answer:</span>
                                <span style={{ fontStyle: 'italic' }}>{currentVerifyResult.submitted_answer}</span>
                              </Box>
                            )}
                            {currentVerifyResult.submitted_drawing && (
                              <Box>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
                                  Your drawing:
                                </Typography>
                                <Box
                                  component="img"
                                  src={currentVerifyResult.submitted_drawing}
                                  alt="Your handwritten answer"
                                  sx={{
                                    maxWidth: '100%',
                                    maxHeight: 180,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: currentVerifyResult.is_correct ? 'success.light' : 'error.light',
                                    display: 'block',
                                    bgcolor: 'white',
                                  }}
                                />
                                {_dp.aiEvaluated && !_dp.needsReview && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                                    ✦ Evaluated by AI Vision
                                  </Typography>
                                )}
                                {_dp.needsReview && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.dark', fontStyle: 'italic' }}>
                                    ⚠ AI models gave conflicting verdicts — ask your tutor to review
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Alert>
                      ) : null; })()}
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </QuestionAnnotationCanvas>

          {/* Navigation — sticky at bottom so Previous/Next always accessible */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
              bgcolor: '#f5f5f5',
              mx: { xs: -1.5, sm: -3 },
              px: { xs: 1.5, sm: 3 },
              pt: 1.5,
              pb: 2,
              mt: 1,
              borderTop: '1px solid #e8e8e8',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => goToQuestion(currentIdx - 1)}
              disabled={currentIdx === 0}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Previous
            </Button>

            <Button
              variant="outlined"
              startIcon={<Schedule />}
              onClick={handleAnswerLater}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#1565c0', borderColor: '#1565c0' }}
            >
              Answer Later
            </Button>

            <Button
              variant="outlined"
              endIcon={<ArrowForward />}
              onClick={() => {
                if (currentIdx >= questions.length - 1) {
                  setShowFinalQuestionDialog(true);
                } else {
                  goToQuestion(currentIdx + 1);
                }
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Next
            </Button>
          </Box>
        </Box>

        {/* Right: Question Palette */}
        <Box
          sx={{
            width: { xs: '100%', lg: 280 },
            flexShrink: 0,
            bgcolor: 'white',
            borderLeft: { xs: 'none', lg: '1px solid #e0e0e0' },
            borderTop: { xs: '1px solid #e0e0e0', lg: 'none' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            maxHeight: { xs: 220, lg: 'none' },
          }}
        >
          {/* Palette header */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
              Question Palette
            </Typography>
            {/* Filter */}
            <FormControl size="small" fullWidth>
              <InputLabel>Filter questions</InputLabel>
              <Select
                value={filterPalette}
                label="Filter questions"
                onChange={(e) => setFilterPalette(e.target.value)}
              >
                <MenuItem value="all">All questions</MenuItem>
                <MenuItem value={STATUS.ANSWERED}>Answered</MenuItem>
                <MenuItem value={STATUS.PARTIAL}>Partly answered</MenuItem>
                <MenuItem value={STATUS.UNANSWERED}>Unanswered</MenuItem>
                <MenuItem value={STATUS.FLAGGED}>Flagged</MenuItem>
                <MenuItem value={STATUS.ANSWER_LATER}>Answer Later</MenuItem>
                <MenuItem value={STATUS.NOT_VISITED}>Not Visited</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Question number grid — grouped by section_title */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
            }}
          >
            {(() => {
              // Build sections: each non-null section_title change starts a new group
              const sections = [];
              questions.forEach((q, i) => {
                const title = displaySectionTitle(q.section_title);
                if (
                  sections.length === 0 ||
                  (title !== null && title !== sections[sections.length - 1].title)
                ) {
                  sections.push({ title, items: [] });
                }
                sections[sections.length - 1].items.push({ q, i });
              });

              // Use pre-computed examPositionByIndex (moved above JSX return for shared use with header)

              return sections.map((section, sIdx) => {
                const visibleItems = section.items.filter(({ q, i }) => {
                  const s = statuses[i] || STATUS.NOT_VISITED;
                  const st = (s === STATUS.FLAGGED || s === STATUS.ANSWER_LATER || s === STATUS.NOT_VISITED)
                    ? s
                    : computeQuestionStatus(q, answers[q.id]);
                  return filterPalette === 'all' || st === filterPalette;
                });
                if (visibleItems.length === 0) return null;
                return (
                  <Box key={sIdx} sx={{ mb: 1.5 }}>
                    {/* Section label — only when there are multiple sections */}
                    {sections.length > 1 && section.title && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          color: '#1565c0',
                          fontSize: '0.68rem',
                          mb: 0.75,
                          mt: sIdx > 0 ? 0.5 : 0,
                          borderBottom: '1px solid #e3f2fd',
                          pb: 0.25,
                        }}
                      >
                        {section.title}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {visibleItems.map(({ q, i }) => {
                        const _s = statuses[i] || STATUS.NOT_VISITED;
                        const st = (_s === STATUS.FLAGGED || _s === STATUS.ANSWER_LATER || _s === STATUS.NOT_VISITED)
                          ? _s
                          : computeQuestionStatus(q, answers[q.id]);
                        const isCurrent = i === currentIdx;
                        const hasDiagramDescOnly =
                          !(q.image_url || (Array.isArray(q.image_urls) && q.image_urls?.length)) &&
                          q.image_description;
                        // Use exam-wide position so sections after a grouped block show correct numbers (e.g. 29–37 not 2–10)
                        const pos = examPositionByIndex.positions[i];
                        const paletteLabel = (() => {
                          if (q.type === 'grouped' && q.sub_questions?.length) {
                            const isComprehension = isComprehensionPassage(q);
                            if (isComprehension) {
                              // English comprehension: show full range e.g. "6–7"
                              const nums = q.sub_questions.map((s) => s.paper_number).filter(Boolean);
                              if (nums.length > 0) return nums.length === 1 ? String(nums[0]) : `${nums[0]}–${nums[nums.length - 1]}`;
                              if (pos) return `${pos.start}–${pos.end}`;
                            }
                            // Math / other subjects: grouped = one question, use paper_number when set
                            const qpn = q.paper_number != null && String(q.paper_number).trim() !== '' ? String(q.paper_number).trim() : null;
                            return qpn || (pos ? String(pos.start) : String(i + 1));
                          }
                          const qpn = q.paper_number != null && String(q.paper_number).trim() !== '' ? String(q.paper_number).trim() : null;
                          return qpn || (pos ? String(pos.start) : String(i + 1));
                        })();
                        const isLongLabel = paletteLabel.length > 3;
                        return (
                          <Box
                            key={i}
                            onClick={() => goToQuestion(i)}
                            sx={{
                              position: 'relative',
                              width: isLongLabel ? 'auto' : 40,
                              minWidth: 40,
                              height: 40,
                              px: isLongLabel ? 1 : 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: isLongLabel ? 2 : '50%',
                              cursor: 'pointer',
                              fontWeight: 700,
                              fontSize: isLongLabel ? '0.65rem' : '0.85rem',
                              bgcolor: statusColors[st],
                              color: st === STATUS.NOT_VISITED ? '#374151' : 'white',
                              border: isCurrent ? '3px solid #1a1a1a' : '2px solid transparent',
                              boxShadow: isCurrent ? '0 0 0 1px #1a1a1a' : 'none',
                              transition: 'all 0.15s',
                              '&:hover': { opacity: 0.85, transform: 'scale(1.1)' },
                            }}
                          >
                            {paletteLabel}
                            {hasDiagramDescOnly && (
                              <ImageNotSupported
                                sx={{
                                  position: 'absolute',
                                  bottom: -2,
                                  right: -2,
                                  fontSize: 12,
                                  color: 'warning.main',
                                  bgcolor: 'white',
                                  borderRadius: '50%',
                                }}
                              />
                            )}
                            {st === STATUS.FLAGGED && (
                              <Flag
                                sx={{
                                  position: 'absolute',
                                  top: -6,
                                  right: -6,
                                  fontSize: 14,
                                  color: '#ff9800',
                                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.4))',
                                  pointerEvents: 'none',
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              });
            })()}
          </Box>

          {/* Legend */}
          <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e0e0e0' }}>
            {isTesterMockExam && totalMarks > 0 && (
              <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Marks attempted
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    {marksAttempted} / {totalMarks}
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min((marksAttempted / totalMarks) * 100, 100)} sx={{ borderRadius: 1, height: 6 }} />
              </Box>
            )}
            {[
              { label: `Answered - ${answeredCount}`, color: statusColors[STATUS.ANSWERED] },
              ...(partialCount > 0 ? [{ label: `Partly answered - ${partialCount}`, color: statusColors[STATUS.PARTIAL] }] : []),
              { label: `Answer later - ${answerLaterCount}`, color: statusColors[STATUS.ANSWER_LATER] },
              { label: `Flagged - ${flaggedCount}`, color: statusColors[STATUS.FLAGGED] },
              { label: `Unanswered - ${unansweredCount}`, color: statusColors[STATUS.UNANSWERED] },
              { label: `Not visited - ${Object.values(statuses).filter((s) => s === STATUS.NOT_VISITED).length}`, color: statusColors[STATUS.NOT_VISITED] },
            ].map(({ label, color }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Instructions button */}
          {examData?.instructions && (
            <Box sx={{ px: 2, pb: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<Bookmark />}
                onClick={() => setInstructionsOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Instructions
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* ---- Tab Switch Warning (anti-cheat) - persistent, cannot dismiss by clicking outside ---- */}
      <Dialog
        open={showTabSwitchWarning}
        onClose={() => {}}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{ sx: { border: '2px solid', borderColor: 'error.main' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>Warning</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            You have switched away from the exam window. This has been recorded. Returning to the exam...
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={() => setShowTabSwitchWarning(false)} sx={{ textTransform: 'none' }}>
            Return to Exam
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Instructions Dialog ---- */}
      <Dialog open={instructionsOpen} onClose={() => setInstructionsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Exam Instructions</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <HtmlWithLatex html={examData?.instructions || ''} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstructionsOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mcqNoticeDialog.open} onClose={() => setMcqNoticeDialog((p) => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Note about this exam</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-line' }}>
            {mcqNoticeDialog.message || "This exam was originally designed for pen and paper. The questions below are difficult to answer online (e.g. drawing tasks) and are recommended to be attempted in a multiple-choice style — do your best, and don't worry if they are hard to complete exactly on screen."}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {mcqNoticeDialog.items.map((item, i) => (
              <Box key={i} sx={{ p: 1, borderRadius: 1, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.label}</Typography>
                {item.note && (
                  <Typography variant="caption" color="text.secondary">{item.note}</Typography>
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setMcqNoticeDialog((p) => ({ ...p, open: false }))} sx={{ textTransform: 'none' }}>
            OK, start the exam
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Time Warning Dialog ---- */}
      <Dialog open={timeWarningOpen} onClose={() => setTimeWarningOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'warning.main' }}>Time Warning</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{timeWarningMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimeWarningOpen(false)} variant="contained" sx={{ textTransform: 'none' }}>
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Confirm Submit Dialog ---- */}
      <Dialog open={confirmSubmit} onClose={() => setConfirmSubmit(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Submit Exam?</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: '#e8f5e9' }}>
              <Typography variant="body2">Answered</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{answeredCount}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: '#fff3e0' }}>
              <Typography variant="body2">Answer Later</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main' }}>{answerLaterCount}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: '#ffebee' }}>
              <Typography variant="body2">Unanswered</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>{unansweredCount}</Typography>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 600, mt: 1, color: 'text.secondary' }}>Review Answers</Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 0.5 }}>
              {questions.map((q, i) => {
                const qId = q.id || i;
                const ans = answers[qId];
                const st = statuses[i] || STATUS.NOT_VISITED;
                const preview = (() => {
                  if (ans == null || ans === '') return '—';
                  // TypeDrawInput answer: { text, drawing }
                  if (typeof ans === 'object' && !Array.isArray(ans) && (ans.text != null || ans.drawing != null)) {
                    const textStr = String(ans.text ?? '').trim();
                    const hasDrawing = !!ans.drawing;
                    if (textStr && hasDrawing) {
                      const preview = textStr.slice(0, 30) + (textStr.length > 30 ? '…' : '');
                      return `${preview} + ✎ Drawing`;
                    }
                    if (textStr) return textStr.slice(0, 40) + (textStr.length > 40 ? '…' : '');
                    if (hasDrawing) return '✎ Drawing submitted';
                    return '—';
                  }
                  // Array answer: multiple-select, ordering, multi-fill
                  if (Array.isArray(ans)) {
                    const filled = ans.filter((a) => a != null && String(a).trim() !== '');
                    if (!filled.length) return '—';
                    const str = filled.map((a) => String(a)).join(', ');
                    return str.slice(0, 40) + (str.length > 40 ? '…' : '');
                  }
                  // Object answer: grouped sub-questions or matching pairs
                  if (typeof ans === 'object') {
                    const entries = Object.entries(ans).filter(([, v]) => v != null && v !== '');
                    if (!entries.length) return '—';
                    if (q.type === 'grouped') {
                      return `${entries.length} sub-question${entries.length !== 1 ? 's' : ''} answered`;
                    }
                    // matching / other object — show first few key→value pairs
                    const str = entries
                      .map(([k, v]) => {
                        const vStr = typeof v === 'object' && v !== null
                          ? (v.text != null ? String(v.text) : JSON.stringify(v))
                          : String(v ?? '');
                        return `${k} → ${vStr}`;
                      })
                      .join('; ');
                    return str.slice(0, 40) + (str.length > 40 ? '…' : '');
                  }
                  return String(ans).slice(0, 40) + (String(ans).length > 40 ? '…' : '');
                })();
                return (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      px: 1,
                      borderRadius: 0.5,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: statusColors[st], flexShrink: 0 }} />
                      <Typography variant="caption" noWrap sx={{ flex: 1 }}>Q{q.paper_number ?? (i + 1)}: {preview}</Typography>
                    </Box>
                    <Button
                      size="small"
                      sx={{ textTransform: 'none', fontSize: '0.7rem', flexShrink: 0 }}
                      onClick={() => { setConfirmSubmit(false); goToQuestion(i); }}
                    >
                      Jump
                    </Button>
                  </Box>
                );
              })}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Once submitted, you cannot make changes. Are you sure?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmit(false)} sx={{ textTransform: 'none' }}>
            Go Back
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSubmit(false)}
            disabled={submitting}
            sx={{ bgcolor: '#ef5350', '&:hover': { bgcolor: '#c62828' }, textTransform: 'none' }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : 'Submit Exam'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- AI Scoring Overlay ---- */}
      {aiScoring.active && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(10,10,30,0.82)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: { xs: 4, sm: 6 },
              maxWidth: 460,
              width: '90%',
              textAlign: 'center',
              borderRadius: 4,
              background: 'linear-gradient(145deg, #ffffff 0%, #f0f4ff 100%)',
            }}
          >
            {/* Animated spinner with AI label */}
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
              <CircularProgress size={80} thickness={3} sx={{ color: 'primary.main' }} />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.9rem', color: 'primary.main' }}>
                  AI
                </Typography>
              </Box>
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1a1a2e' }}>
              AI Scoring in Progress
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5, lineHeight: 1.6 }}>
              Our AI is carefully analysing your answers for accuracy, partial credit and quality feedback.
            </Typography>

            {/* Progress bar */}
            <LinearProgress
              variant="determinate"
              value={aiScoring.progress}
              sx={{
                height: 10,
                borderRadius: 5,
                mb: 1.5,
                bgcolor: '#e3e8ff',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  background: 'linear-gradient(90deg, #374151, #818cf8)',
                },
              }}
            />

            {/* Dynamic stage message */}
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
              {aiScoring.progress < 25
                ? 'Parsing your answers…'
                : aiScoring.progress < 50
                ? 'Evaluating each response…'
                : aiScoring.progress < 75
                ? 'Checking for partial credit…'
                : aiScoring.progress < 95
                ? 'Calculating your AI score…'
                : 'Finalising results…'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This usually takes 15–120 seconds. Please wait.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* ---- Leave Exam Confirmation Dialog (browser back button) ---- */}
      <Dialog
        open={leaveDialogOpen}
        onClose={() => setLeaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { border: '2px solid', borderColor: 'warning.main' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'warning.main' }}>Leave Exam?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to leave? Your answers have been auto-saved and you can resume
            this exam later.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The exam will be <strong>paused</strong> while you are away — the timer will not run
            until you resume.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => setLeaveDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Stay on Exam
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => {
              // Mark exam as paused so the exam list shows the "Paused" card state
              // and the timer does not count down while the student is away.
              sessionStorage.setItem(`examPaused_${examId}`, '1');
              const dest = pendingNavRef.current ?? '/student/mock-exams';
              pendingNavRef.current = null;
              isLeavingRef.current = true;
              setLeaveDialogOpen(false);
              navigate(dest);
            }}
            sx={{ textTransform: 'none' }}
          >
            Leave &amp; Pause
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Flag Reason Dialog (small popup when Flag to Review clicked) ---- */}
      <Dialog
        open={flagReasonDialog.open}
        onClose={() => setFlagReasonDialog(EMPTY_FLAG_DIALOG)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Flag for Review</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the issue type(s) and/or add a brief reason — all optional.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={flagReasonDialog.issue_question}
                onChange={(e) => setFlagReasonDialog((p) => ({ ...p, issue_question: e.target.checked }))}
              />
            }
            label="Question issue"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={flagReasonDialog.issue_answer}
                onChange={(e) => setFlagReasonDialog((p) => ({ ...p, issue_answer: e.target.checked }))}
              />
            }
            label="Answer issue"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={flagReasonDialog.issue_image}
                onChange={(e) => setFlagReasonDialog((p) => ({ ...p, issue_image: e.target.checked }))}
              />
            }
            label="Image issue"
          />
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ '& .ql-container': { minHeight: 100, fontSize: '0.9rem' }, '& .ql-editor.ql-blank::before': { fontStyle: 'normal', color: '#9e9e9e' } }}>
            <ReactQuill
              theme="snow"
              value={flagReasonDialog.reason}
              onChange={(html) => processFlagReasonHtml(html, (v) => setFlagReasonDialog((p) => ({ ...p, reason: v })))}
              modules={FLAG_QUILL_MODULES}
              formats={FLAG_QUILL_FORMATS}
              placeholder="e.g. Unclear wording, possible typo, wrong answer..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setFlagReasonDialog(EMPTY_FLAG_DIALOG)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFlagReasonSave}
            disabled={flaggingInProgress[currentIdx]}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {flaggingInProgress[currentIdx] ? <CircularProgress size={16} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Final Question Next Button Dialog ---- */}
      <Dialog open={showFinalQuestionDialog} onClose={() => setShowFinalQuestionDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center', fontSize: '1.25rem' }}>
          Final Question
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
              You are on the last question. Do you want to go to the next question or submit the exam?
            </Typography>

            {/* Summary Stats */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: '#e8f5e9' }}>
                <Typography variant="body2">Answered</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{answeredCount}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: '#fff3e0' }}>
                <Typography variant="body2">Answer Later</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main' }}>{answerLaterCount}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: '#ffebee' }}>
                <Typography variant="body2">Unanswered</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>{unansweredCount}</Typography>
              </Box>
              {flaggedCount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: '#fff8e1' }}>
                  <Typography variant="body2">Flagged for Review</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main' }}>{flaggedCount}</Typography>
                </Box>
              )}
            </Box>

            {/* Review Answer Later Option */}
            {answerLaterCount > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reviewAnswerLater}
                    onChange={(e) => setReviewAnswerLater(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Go to Answer Later questions before submitting
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            )}

            {/* Review Flagged Option */}
            {flaggedCount > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reviewFlagged}
                    onChange={(e) => setReviewFlagged(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Review flagged questions before submitting
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => setShowFinalQuestionDialog(false)}
            sx={{ textTransform: 'none' }}
          >
            Go Back
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowFinalQuestionDialog(false);
                handleAnswerLater();
              }}
              sx={{ textTransform: 'none' }}
            >
              Answer Later
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (reviewAnswerLater && answerLaterCount > 0) {
                  // Find first Answer Later question and navigate to it
                  const firstAnswerLaterIdx = Object.entries(statuses).find(([, status]) => status === STATUS.ANSWER_LATER)?.[0];
                  if (firstAnswerLaterIdx !== undefined) {
                    setShowFinalQuestionDialog(false);
                    setReviewAnswerLater(false);
                    goToQuestion(parseInt(firstAnswerLaterIdx, 10));
                    return;
                  }
                }
                if (reviewFlagged && flaggedCount > 0) {
                  // Find first flagged question and navigate to it
                  const firstFlaggedIdx = Object.entries(statuses).find(([, status]) => status === STATUS.FLAGGED)?.[0];
                  if (firstFlaggedIdx !== undefined) {
                    setShowFinalQuestionDialog(false);
                    goToQuestion(parseInt(firstFlaggedIdx, 10));
                    return;
                  }
                }
                setShowFinalQuestionDialog(false);
                setConfirmSubmit(true);
              }}
              sx={{ bgcolor: '#ef5350', '&:hover': { bgcolor: '#c62828' }, textTransform: 'none' }}
            >
              Submit Exam
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
    <VirtualProtractorOverlay visible={showProtractor} />
    <VirtualRulerOverlay visible={showRuler} pxPerCm={activePxPerCm} />
    <VirtualSetSquareOverlay visible={showSetSquare} />
    <VirtualCompassOverlay visible={showCompass} pxPerCm={activePxPerCm} />
    {canVerifyMockQuestion && currentQuestion && (
      <QuestionVerifyDialog
        open={mockVerifyDialogOpen}
        question={mockVerifyQuestionFull ?? currentQuestion}
        loading={mockVerifyQuestionLoading}
        onClose={() => setMockVerifyDialogOpen(false)}
        onSubmit={async ({ status, notes, platforms }) => {
          const bankId = String(currentQuestion.id).replace(/^bank_/, '');
          const ok = await submitMockVerification({ type: "question", id: bankId, status, notes, platforms });
          if (ok) {
            setMockVerifyDialogOpen(false);
            const isLastQuestion = currentIdx === questions.length - 1;
            if (isLastQuestion && canVerifyMockExam && examId) {
              setVerifyExamPlatforms(platforms?.length ? platforms : ['web']);
              setVerifyExamDialogOpen(true);
            }
          }
        }}
        onEdit={mockVerifyQuestionFull ? (q) => {
          setMockVerifyDialogOpen(false);
          setEditFromVerifyQuestion(q);
          setEditFromVerifyOpen(true);
        } : undefined}
        itemData={getMockItemData("question", String(currentQuestion.id).replace(/^bank_/, ''))}
        submitting={mockVerifySubmitting}
      />
    )}
    {canVerifyMockExam && examId && (
      <Dialog open={verifyExamDialogOpen} onClose={() => setVerifyExamDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark entire exam as verified?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You verified the last question. Do you want to mark the whole exam <strong>{examData?.name}</strong> as approved
            on <strong>{verifyExamPlatforms.join(' + ')}</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The exam only counts as fully verified once both web and mobile have signed it off.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyExamDialogOpen(false)} disabled={verifyExamSubmitting}>Skip</Button>
          <Button
            variant="contained"
            color="error"
            disabled={verifyExamSubmitting}
            onClick={async () => {
              setVerifyExamSubmitting(true);
              try {
                await api.post('/admin/content-verifications', {
                  verifiable_type: 'mock_exam',
                  verifiable_id: parseInt(examId, 10),
                  status: 'approved',
                  notes: null,
                  platforms: verifyExamPlatforms,
                });
                toast.success('Exam marked as approved.');
              } catch {
                toast.error('Failed to mark exam as verified.');
              } finally {
                setVerifyExamSubmitting(false);
                setVerifyExamDialogOpen(false);
              }
            }}
          >
            {verifyExamSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Approve exam'}
          </Button>
        </DialogActions>
      </Dialog>
    )}
    {editFromVerifyOpen && editFromVerifyQuestion && (
      <QuestionBankQuestionEditDialog
        question={editFromVerifyQuestion}
        saving={editFromVerifySaving}
        onClose={() => { setEditFromVerifyOpen(false); setEditFromVerifyQuestion(null); }}
        onSave={async (payload) => {
          const savedIdx = currentIdxRef.current;
          setEditFromVerifySaving(true);
          try {
            const res = await api.put(`/admin/question-bank/${editFromVerifyQuestion.id}`, payload);
            const updated = res.data?.data;
            if (updated) {
              setQuestions((prev) => prev.map((q) => {
                if (String(q.id).replace(/^bank_/, '') === String(editFromVerifyQuestion.id)) {
                  const nextImgUrl  = 'image_url'  in updated ? updated.image_url  : q.image_url;
                  const nextImgUrls = 'image_urls' in updated ? updated.image_urls : q.image_urls;
                  return {
                    ...q,
                    default_marks: updated.default_marks ?? q.default_marks,
                    marks:         updated.default_marks ?? q.marks,
                    question_text: updated.question_text ?? q.question_text,
                    image_url:  nextImgUrl,
                    image_urls: nextImgUrls,
                  };
                }
                return q;
              }));
            }
            setCurrentIdx(savedIdx);
            toast.success('Question saved.');
            setEditFromVerifyOpen(false);
            setEditFromVerifyQuestion(null);
          } catch (err) {
            const errs = err?.response?.data?.errors;
            const first = errs ? Object.values(errs)[0]?.[0] : null;
            toast.error(first || err?.response?.data?.message || 'Failed to save question.');
          } finally {
            setEditFromVerifySaving(false);
          }
        }}
      />
    )}
    </MeasurementToolsProvider>
    </AnnotateProvider>
  );
};

export default StudentMockExamTake;
